import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import FingerprintScanner from '../common/FingerprintScanner';
import { 
  Form, 
  Button, 
  Row, 
  Col, 
  ListGroup, 
  Card, 
  Modal, 
  Image,
  Table,
  Alert,
  Badge,
  Spinner
} from 'react-bootstrap';
import { FaFingerprint, FaCamera, FaTimes, FaTrash, FaList, FaPlus } from 'react-icons/fa';
import baseUrl from '../../baseURL'; // Import base URL
import { fingerprintService } from '../../services/fingerprintService';
import axios from 'axios'; // Import axios
import { FaEye } from 'react-icons/fa'; // Import eye icon from react-icons
import { imageURL } from '../../imageURL'; // Correctly import the named export
import GirviPageShell from '../common/GirviPageShell';
import '../../styles/create-pledge.css';
import LedgerMaster from './master/LedgerMaster';
import { printPledge } from '../../utils/printUtility';
import { compressImageToFile } from '../../utils/compressImage';
import { resolvePledgePhotoUrl } from '../../utils/pledgePhotoUrl';
import { computeArticleTypeWeightTotals } from '../../utils/pledgeArticleWeights';
import { blackFormRate, isBlackLogin, rateLabel } from '../../utils/blackInterest';

const PLEDGE_PHOTO_MAX_KB = 5;

/** Prefer client photo, then Aadhar / PAN from ledger photos. */
function getClientStoredPhoto(client) {
  if (!client?.photos) return null;
  return client.photos.clientPhoto || client.photos.aadharCard || client.photos.panCard || null;
}

function getClientPhotoUrl(client) {
  return resolvePledgePhotoUrl(getClientStoredPhoto(client));
}

function parseLocaleAmount(value) {
  if (value === '' || value == null) return NaN;
  const s = String(value).replace(/,/g, '').replace(/\s/g, '').trim();
  if (s === '') return NaN;
  return parseFloat(s);
}

function formatInrAmount(value) {
  const n = typeof value === 'number' && !Number.isNaN(value) ? value : parseLocaleAmount(value);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatWeightThreeDecimals(value) {
  if (value === '' || value == null) return '';
  const n = parseFloat(String(value).replace(/,/g, ''));
  if (Number.isNaN(n)) return '';
  return n.toFixed(3);
}

function formatPledgeDateDDMMYYYY(isoDateStr) {
  if (!isoDateStr) return '';
  const parts = String(isoDateStr).split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    if (y && m && d) return `${d}-${m}-${y}`;
  }
  return String(isoDateStr);
}

/** API / DB date → dd-mm-yyyy (uses calendar date in local TZ when parsing ISO). */
function formatPledgeDateFromApi(d) {
  if (d == null || d === '') return '';
  if (typeof d === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  }
  const dt = d instanceof Date ? d : new Date(d);
  if (!Number.isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const mo = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${day}-${mo}-${y}`;
  }
  return String(d);
}

function sanitizeDecimalInput(raw) {
  let v = String(raw).replace(/[^\d.]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
  }
  return v;
}

function formatCustomerName(customer) {
  if (!customer) return '';
  const up = (v) => String(v || '').trim().toUpperCase();
  const rel = up(customer.relation);
  const showRel = rel && rel !== 'F/O';
  return [
    up(customer.firstName),
    showRel ? rel : '',
    showRel ? up(customer.relationName) : '',
    up(customer.lastName)
  ].filter(Boolean).join(' ');
}

function sanitizeAmountInput(raw) {
  let v = String(raw).replace(/[^\d.,]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
  }
  return v;
}

const CreatePledge = ({
  createEndpoint = null,
  suggestedTicketEndpoint = null,
  secondaryOnlyMode = false,
  skipAccountingOnCreate = false,
  postAccountingOnEdit = false
} = {}) => {
  const [searchParams] = useSearchParams();
  const editPledgeId = searchParams.get('edit');
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [pledgeData, setPledgeData] = useState({
    customerCode: '',
    articles: [
      {
        articleId: '',
        description: '',
        valueTotal: '',
        grossWeight: '',
        netWeight: '',
        purity: ''
      }
    ],
    loanDetails: {
      principalAmount: '',
      duration: '12',
      rateOfInterest: '',
      monthlyInterest: '',
      paidExcess: false
    },
    fingerprint: null,
    clientPhoto: null,
    articlePhoto: null
  });

  const [disbursementPaymentModes, setDisbursementPaymentModes] = useState([
    { type: 'cash', amount: '', bankLedgerId: '', bankLedgerName: '', bankLedgerCode: '' }
  ]);
  const [bankAccountsList, setBankAccountsList] = useState([]);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [photoType, setPhotoType] = useState(''); // 'client' or 'article'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const [isFingerprintScannerOpen, setIsFingerprintScannerOpen] = useState(false);
  const [fingerprintData, setFingerprintData] = useState(null);
  const [isCapturingFingerprint, setIsCapturingFingerprint] = useState(false);
  const [fingerprintError, setFingerprintError] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const fingerprintCanvasRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [articles, setArticles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [clientPledges, setClientPledges] = useState([]);
  const [showPledges, setShowPledges] = useState(false);
  const [showPledgesModal, setShowPledgesModal] = useState(false);
  const [loadingPledges, setLoadingPledges] = useState(false);
  const [hasMultipleArticles, setHasMultipleArticles] = useState(false);
  const [showRateOfInterestPopup, setShowRateOfInterestPopup] = useState(false);
  const [isRateOfInterestFocused, setIsRateOfInterestFocused] = useState(false);
  const [monthlyInterestAmount, setMonthlyInterestAmount] = useState('');
  const [functionalKey, setFunctionalKey] = useState('');
  const [functionalKeys, setFunctionalKeys] = useState([]);
  const [showPledgeDetails, setShowPledgeDetails] = useState(false);
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [showTicketPopup, setShowTicketPopup] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  // New pledge flow: Submit -> TicketNo (Enter) -> Edit (Enter) -> Confirm popup (Enter=Generate / N=Edit)
  const [flowStep, setFlowStep] = useState('FORM'); // FORM | TICKET | EDIT | CONFIRM
  const [draftPledgeId, setDraftPledgeId] = useState(null);
  const [ticketNoInput, setTicketNoInput] = useState('');
  const ticketNoInputRef = useRef(null);
  const customerSearchRef = useRef(null);
  const principalAmountRef = useRef(null);
  const durationRef = useRef(null);
  const rateOfInterestRef = useRef(null);
  const legalRateRef = useRef('');
  const editSecondaryRef = useRef(null);
  const formScrollRef = useRef(null);
  const articleFieldRefs = useRef([]);
  const submitPledgeBtnRef = useRef(null);
  const addCustomerModalCleanupRef = useRef(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState(null);
  const [confirmPhotoUrls, setConfirmPhotoUrls] = useState({ client: null, article: null });
  const [showTicketTakenPopup, setShowTicketTakenPopup] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  // Force LedgerMaster to fully remount each time Add Customer modal is reopened
  const [addCustomerModalInstance, setAddCustomerModalInstance] = useState(0);
  const [newCustomer, setNewCustomer] = useState(null);
  const [pledgeDate, setPledgeDate] = useState(new Date().toISOString().split('T')[0]); // Current date in YYYY-MM-DD format
  const [scanningForCustomer, setScanningForCustomer] = useState(false);

  const revokeConfirmPhotoUrls = (urls = confirmPhotoUrls) => {
    if (urls?.client) URL.revokeObjectURL(urls.client);
    if (urls?.article) URL.revokeObjectURL(urls.article);
  };

  const closeConfirmPopup = () => {
    revokeConfirmPhotoUrls();
    setConfirmPhotoUrls({ client: null, article: null });
    setShowConfirmPopup(false);
    setFlowStep('EDIT');
  };

  const stopCameraStream = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Start camera after modal mounts (video ref must exist)
  useEffect(() => {
    if (!isCameraOpen || showPhotoPreview) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        setError('Could not access camera. Allow permission or use Choose File.');
        setIsCameraOpen(false);
        setShowPhotoPreview(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopCameraStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraOpen, showPhotoPreview]);

  // Keyboard shortcuts inside confirm popup: Enter = Generate, N = Edit
  useEffect(() => {
    if (!showConfirmPopup) return;
    const onKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleGenerateFromPopup();
      } else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        closeConfirmPopup();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmPopup]);

  // Ticket taken popup: Enter closes popup and focuses Ticket No
  useEffect(() => {
    if (!showTicketTakenPopup) return;
    const onKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setShowTicketTakenPopup(false);
        setTimeout(() => ticketNoInputRef.current?.focus(), 50);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showTicketTakenPopup]);

  const fetchSuggestedTicketNo = async () => {
    try {
      const token = localStorage.getItem('token');
      const url =
        suggestedTicketEndpoint ||
        `${baseUrl}/pledges/suggested${secondaryOnlyMode ? '-secondary' : ''}-ticket-no`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success && data?.suggestedTicketNo) {
        setTicketNoInput(String(data.suggestedTicketNo));
      }
    } catch (err) {
    }
  };

  // Show next ticket no on opening Create Pledge (suggestion)
  useEffect(() => {
    if (flowStep !== 'FORM') return;
    if (draftPledgeId) return;
    if ((ticketNoInput || '').trim()) return; // don't override user input
    fetchSuggestedTicketNo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildDisbursementPaymentModesPayload = () => {
    const principalNum = parseLocaleAmount(pledgeData.loanDetails.principalAmount) || 0;
    const rows = disbursementPaymentModes
      .map((r) => ({
        type: r.type || 'cash',
        bankLedgerId: r.bankLedgerId || null,
        bankLedgerCode: r.bankLedgerCode || null,
        bankLedgerName: (r.bankLedgerName || '').trim() || null,
        amount: parseLocaleAmount(r.amount) || 0
      }))
      .filter((r) => r.amount > 0);
    if (rows.length === 0 && principalNum > 0) {
      return [{ type: 'cash', bankLedgerId: null, bankLedgerCode: null, bankLedgerName: null, amount: principalNum }];
    }
    return rows;
  };

  const handleDisbursementRowChange = (index, field, value) => {
    setDisbursementPaymentModes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddDisbursementRow = () => {
    setDisbursementPaymentModes((prev) => [
      ...prev,
      { type: 'cash', amount: '', bankLedgerId: '', bankLedgerName: '', bankLedgerCode: '' }
    ]);
  };

  const handleRemoveDisbursementRow = (index) => {
    setDisbursementPaymentModes((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const buildUpdatePayload = () => {
    const ticketNo = (ticketNoInput || '').trim();
    const payload = {
      // NOTE: update endpoint expects schema-shaped fields (customer is embedded),
      // so we do not send customerCode here.
      date: pledgeDate,
      articles: (pledgeData.articles || []).map((a) => ({
        ...a,
        valueTotal: parseLocaleAmount(a.valueTotal) || 0,
        grossWeight:
          a.grossWeight === '' || a.grossWeight == null
            ? null
            : parseFloat(String(a.grossWeight).replace(/,/g, '')),
        netWeight:
          a.netWeight === '' || a.netWeight == null
            ? null
            : parseFloat(String(a.netWeight).replace(/,/g, '')),
        purity: a.purity,
      })),
      loanDetails: {
        principalAmount: parseLocaleAmount(pledgeData.loanDetails.principalAmount) || 0,
        duration: Number(pledgeData.loanDetails.duration) || 0,
        rateOfInterest: pledgeData.loanDetails.rateOfInterest === '' ? undefined : Number(pledgeData.loanDetails.rateOfInterest),
        paidExcess: !!pledgeData.loanDetails.paidExcess,
        ticketNo,
      },
      fingerprint: pledgeData.fingerprint || null,
      disbursementPaymentModes: buildDisbursementPaymentModesPayload(),
      monthlyInterestAmount: parseFloat(pledgeData.loanDetails.monthlyInterest) || 0,
      functionalKey: functionalKey || null,
    };

    // When editing an existing pledge from the "normal create pledge" page,
    // allow backend to post accounting entries for pledges created with no-accounting page.
    if (isEditMode && postAccountingOnEdit) {
      payload.postAccountingEntries = true;
    }

    return payload;
  };

  const buildConfirmPreview = () => {
    const totalValue = (pledgeData.articles || []).reduce(
      (sum, a) => sum + (parseLocaleAmount(a.valueTotal) || 0),
      0
    );
    const totalGrossWeight = (pledgeData.articles || []).reduce(
      (sum, a) => sum + (parseFloat(String(a.grossWeight || '').replace(/,/g, '')) || 0),
      0
    );
    const totalNetWeight = (pledgeData.articles || []).reduce(
      (sum, a) => sum + (parseFloat(String(a.netWeight || '').replace(/,/g, '')) || 0),
      0
    );
    
    const articleTypeTotals = computeArticleTypeWeightTotals(pledgeData.articles, articles);

    return {
      ticketNo: ticketNoInput,
      date: pledgeDate,
      customer: selectedClient,
      articles: pledgeData.articles,
      loanDetails: pledgeData.loanDetails,
      totals: { totalValue, totalGrossWeight, totalNetWeight },
      articleTypeTotals
    };
  };

  // Enter on Ticket No after Submit: validate availability and then show confirm popup (no saving here)
  const handleTicketEnter = async () => {
    try {
      const ticket = (ticketNoInput || '').trim();
      if (!ticket) {
        setError('Ticket No is required.');
        return;
      }
      if (!draftPledgeId) {
        setError('Please submit the pledge first.');
        return;
      }

      const ok = await validateTicketAvailableForDraft();
      if (!ok) return;

      setError('');
      setMessage(`Press Enter to Generate or N to Edit.`);
      setFlowStep('CONFIRM');
      handleOpenConfirmPopup();
    } catch (err) {
      setError(err?.message || 'Ticket validation failed');
      setTimeout(() => ticketNoInputRef.current?.focus(), 50);
    }
  };

  const handleOpenConfirmPopup = () => {
    revokeConfirmPhotoUrls();
    const preview = buildConfirmPreview();
    const urls = { client: null, article: null };
    if (pledgeData.clientPhoto instanceof File) {
      urls.client = URL.createObjectURL(pledgeData.clientPhoto);
    }
    if (pledgeData.articlePhoto instanceof File) {
      urls.article = URL.createObjectURL(pledgeData.articlePhoto);
    }
    setConfirmPhotoUrls(urls);
    setConfirmPreview(preview);
    setShowConfirmPopup(true);
    setFlowStep('CONFIRM');
  };

  const validateTicketAvailableForDraft = async () => {
    const ticket = (ticketNoInput || '').trim();
    if (!ticket) {
      setError('Ticket No is required.');
      return false;
    }
    if (!draftPledgeId) {
      setError('Please submit the pledge first.');
      return false;
    }

    const token = localStorage.getItem('token');
    const checkRes = await fetch(`${baseUrl}/pledges/ticket-only/${encodeURIComponent(ticket)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (checkRes.ok) {
      const foundData = await checkRes.json();
      const foundPledge = foundData?.pledge || foundData?.data || foundData;
      if (foundPledge?._id && String(foundPledge._id) !== String(draftPledgeId)) {
        setShowTicketTakenPopup(true);
        setTimeout(() => ticketNoInputRef.current?.focus(), 50);
        return false;
      }
      return true;
    }

    if (checkRes.status === 404) return true;

    const err = await checkRes.json().catch(() => ({}));
    throw new Error(err?.message || 'Error checking ticket number');
  };

  const handleSaveDraft = async () => {
    try {
      if (!draftPledgeId) {
        setError('No pledge draft found. Please submit pledge first.');
        return;
      }
      const token = localStorage.getItem('token');
      const payload = buildUpdatePayload();
      const res = await fetch(`${baseUrl}/pledges/${draftPledgeId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to save pledge');
      setError('');
      setMessage('Pledge saved. Press Enter on Ticket No to preview (Generate/Edit).');
      setFlowStep('TICKET');
      setTimeout(() => ticketNoInputRef.current?.focus(), 50);
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const handleGenerateFromPopup = async () => {
    try {
      if (!draftPledgeId) {
        setError('No pledge draft found. Please submit pledge first.');
        return;
      }
      const token = localStorage.getItem('token');

      // Save edits before generating/finalizing
      const payload = buildUpdatePayload();
      const res = await fetch(`${baseUrl}/pledges/${draftPledgeId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to save pledge updates');

      const updatedPledge = data?.data || null;

      setShowConfirmPopup(false);
      setFlowStep('FORM');
      setDraftPledgeId(null);
      setTicketNoInput('');
      setConfirmPreview(null);

      // Reset form (same as your previous post-submit reset)
      setPledgeData({
        customerCode: '',
        articles: [{
          articleId: '',
          description: '',
          valueTotal: '',
          grossWeight: '',
          netWeight: '',
          purity: ''
        }],
        loanDetails: {
          principalAmount: '',
          duration: '12',
          rateOfInterest: '',
          monthlyInterest: '',
          paidExcess: false
        },
        fingerprint: null
      });
      setDisbursementPaymentModes([
        { type: 'cash', amount: '', bankLedgerId: '', bankLedgerName: '', bankLedgerCode: '' }
      ]);
      setSelectedClient(null);
      setFingerprintData(null);
      setIsFingerprintScannerOpen(false);
      setFunctionalKey('');
      setMonthlyInterestAmount('');
      setHasMultipleArticles(false);
      setMessage('Pledge generated successfully.');
      setError('');

      // Ask for print (pledge ticket uses existing printer size config for pledge)
      if (updatedPledge && window.confirm('Print pledge ticket?')) {
        const forPrint = {
          ...updatedPledge,
          articles: (updatedPledge.articles || []).map((a) => {
            const master = articles.find(m => m._id === a.articleId);
            return {
              ...a,
              grossWt: a.grossWt ?? a.grossWeight,
              netWt: a.netWt ?? a.netWeight,
              articleTitle: master?.title || a.articleTitle || a.title
            };
          })
        };
        printPledge(forPrint).catch((err) => {
          setError('Print failed. You can print later from the pledge list.');
        });
      }

      // After clearing, show the next suggested ticket no again
      setTimeout(() => {
        fetchSuggestedTicketNo();
        setTimeout(() => ticketNoInputRef.current?.focus(), 50);
      }, 50);
    } catch (err) {
      setError(err?.message || 'Generate failed');
    }
  };

  const handleCameraOpen = (type) => {
    setPhotoType(type);
    setShowPhotoPreview(false);
    if (capturedPhoto?.url) URL.revokeObjectURL(capturedPhoto.url);
    setCapturedPhoto(null);
    setIsCameraOpen(true);
  };


const handleFingerprintCapture = async () => {
  try {
    setIsCapturingFingerprint(true);
    setFingerprintError('');

    // Capture fingerprint
    const result = await fingerprintService.capture();
    
    // Store the fingerprint data
    setFingerprintData(result);
    setPledgeData(prev => ({
      ...prev,
      fingerprint: {
        template: result.template,
        image: result.image,
        quality: result.quality
      }
    }));
    
    setIsFingerprintScannerOpen(false);
  } catch (error) {
    setFingerprintError(error.message || 'Failed to capture fingerprint. Please try again.');
  } finally {
    setIsCapturingFingerprint(false);
  }
};

  const handleTakePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !video.videoWidth) {
      setError('Camera not ready. Wait a moment and try again.');
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) {
        setError('Could not capture photo. Try again.');
        return;
      }
      const file = new File([blob], `${photoType}-photo.jpg`, { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      setCapturedPhoto({ file, url });
      setShowPhotoPreview(true);
      stopCameraStream();
    }, 'image/jpeg', 0.92);
  };

  const handleConfirmPhoto = async () => {
    if (!capturedPhoto?.file) return;
    try {
      const compressed = await compressImageToFile(
        capturedPhoto.file,
        PLEDGE_PHOTO_MAX_KB,
        220,
        220,
        0.7
      );
      setPledgeData(prev => ({
        ...prev,
        [photoType === 'client' ? 'clientPhoto' : 'articlePhoto']: compressed
      }));
      setError('');
    } catch (err) {
      setError(err?.message || `Could not compress photo to ${PLEDGE_PHOTO_MAX_KB} KB. Try again or use another image.`);
    }
    if (capturedPhoto?.url) URL.revokeObjectURL(capturedPhoto.url);
    setCapturedPhoto(null);
    setShowPhotoPreview(false);
    handleCameraClose();
  };

  const handleRetakePhoto = () => {
    if (capturedPhoto?.url) URL.revokeObjectURL(capturedPhoto.url);
    setCapturedPhoto(null);
    setShowPhotoPreview(false);
  };

  const handleFileChange = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImageToFile(file, PLEDGE_PHOTO_MAX_KB, 220, 220, 0.7);
      setPledgeData(prev => ({ ...prev, [field]: compressed }));
      setError('');
    } catch (err) {
      setError(err?.message || `Could not compress photo to ${PLEDGE_PHOTO_MAX_KB} KB. Choose another image.`);
    }
    e.target.value = '';
  };

  const handleCameraClose = () => {
    stopCameraStream();
    setIsCameraOpen(false);
    setShowPhotoPreview(false);
    if (capturedPhoto?.url) URL.revokeObjectURL(capturedPhoto.url);
    setCapturedPhoto(null);
  };

  // Fetch pledge data for edit mode
  useEffect(() => {
    const fetchPledgeForEdit = async () => {
      if (editPledgeId) {
        try {
          setIsEditMode(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${baseUrl}/pledges/${editPledgeId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            const pledge = data.pledge || data.data || data;
            const secondaryTransaction = data.secondaryTransaction || pledge?.secondaryTransaction || null;
            editSecondaryRef.current = secondaryTransaction;
            
            if (pledge) {
              // Set customer code
              if (pledge.customer?.code) {
                setPledgeData(prev => ({
                  ...prev,
                  customerCode: pledge.customer.code
                }));
                
                // Fetch the client
                try {
                  const clientResponse = await fetch(`${baseUrl}/ledgers?code=${pledge.customer.code}`);
                  const clientData = await clientResponse.json();
                  if (clientData.data && clientData.data.length > 0) {
                    setSelectedClient(clientData.data[0]);
                  } else if (pledge.customer) {
                    setSelectedClient(pledge.customer);
                  }
                } catch (err) {
                  if (pledge.customer) {
                    setSelectedClient(pledge.customer);
                  }
                }
              }

              // Set articles
              if (pledge.articles && pledge.articles.length > 0) {
                setPledgeData(prev => ({
                  ...prev,
                  articles: pledge.articles.map(article => ({
                    articleId: article.articleId || '',
                    description: article.description || '',
                    valueTotal:
                      article.valueTotal === '' || article.valueTotal == null
                        ? ''
                        : formatInrAmount(article.valueTotal),
                    grossWeight:
                      article.grossWeight === '' || article.grossWeight == null
                        ? ''
                        : formatWeightThreeDecimals(article.grossWeight),
                    netWeight:
                      article.netWeight === '' || article.netWeight == null
                        ? ''
                        : formatWeightThreeDecimals(article.netWeight),
                    purity: article.purity || ''
                  }))
                }));
              }

              // Set loan details
              if (pledge.loanDetails) {
                const legalRate = pledge.loanDetails.rateOfInterest ?? '';
                legalRateRef.current = legalRate === '' || legalRate == null ? '' : String(legalRate);
                const black = isBlackLogin();
                const illegalRate = black
                  ? blackFormRate({ ...pledge, secondaryTransaction })
                  : '';
                const monthlyFromBlack = Number(secondaryTransaction?.loanDetails?.proportionalCompoundMonthlyTotal);
                setPledgeData(prev => ({
                  ...prev,
                  loanDetails: {
                    principalAmount:
                      pledge.loanDetails.principalAmount === '' ||
                      pledge.loanDetails.principalAmount == null
                        ? ''
                        : formatInrAmount(pledge.loanDetails.principalAmount),
                    duration: pledge.loanDetails.duration || '12',
                    rateOfInterest: black
                      ? (illegalRate || legalRateRef.current)
                      : (legalRate || ''),
                    monthlyInterest: black && monthlyFromBlack > 0
                      ? monthlyFromBlack
                      : (pledge.loanDetails.monthlyInterest || pledge.loanDetails.monthlyInterestAmount || ''),
                    paidExcess: pledge.loanDetails.paidExcess || false
                  }
                }));
                if (black && monthlyFromBlack > 0) setMonthlyInterestAmount(String(monthlyFromBlack));
              }

              if (Array.isArray(pledge.disbursementPaymentModes) && pledge.disbursementPaymentModes.length > 0) {
                setDisbursementPaymentModes(
                  pledge.disbursementPaymentModes.map((r) => ({
                    type: r.type || 'cash',
                    amount:
                      r.amount === '' || r.amount == null ? '' : formatInrAmount(r.amount),
                    bankLedgerId: r.bankLedgerId != null ? String(r.bankLedgerId) : '',
                    bankLedgerName: r.bankLedgerName || '',
                    bankLedgerCode: r.bankLedgerCode || ''
                  }))
                );
              } else {
                setDisbursementPaymentModes([
                  { type: 'cash', amount: '', bankLedgerId: '', bankLedgerName: '', bankLedgerCode: '' }
                ]);
              }

              // Set date
              if (pledge.date) {
                const date = new Date(pledge.date);
                setPledgeDate(date.toISOString().split('T')[0]);
              }
            }
          } else {
            setError('Failed to load pledge data for editing');
          }
        } catch (error) {
          setError('Failed to load pledge data for editing');
        }
      }
    };

    fetchPledgeForEdit();
  }, [editPledgeId, baseUrl]);

  useEffect(() => {
    if (!isBlackLogin() || !editPledgeId) return;
    const first = pledgeData.articles?.[0]?.articleId;
    const articleId = first && typeof first === 'object' ? first._id : first;
    if (!articleId || !articles.length) return;
    const article = articles.find((row) => String(row._id) === String(articleId));
    const illegalRate = blackFormRate(
      { secondaryTransaction: editSecondaryRef.current, loanDetails: pledgeData.loanDetails },
      article
    );
    if (!illegalRate) return;
    setPledgeData((prev) => {
      const current = String(prev.loanDetails.rateOfInterest ?? '');
      const legal = String(legalRateRef.current ?? '');
      if (current === String(illegalRate)) return prev;
      if (current !== '' && current !== legal) return prev;
      return {
        ...prev,
        loanDetails: { ...prev.loanDetails, rateOfInterest: illegalRate }
      };
    });
  }, [articles, editPledgeId, pledgeData.articles, pledgeData.loanDetails]);

  useEffect(() => {
    const loadBankLedgers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseUrl}/ledgers/bank-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success && Array.isArray(json.data)) {
          setBankAccountsList(json.data);
        }
      } catch (err) {
      }
    };
    loadBankLedgers();
  }, [baseUrl]);

  // Fetch functional keys (F1–F5) for illegal rate on VPSB
  useEffect(() => {
    const fetchFunctionalKeys = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}/pledges/functional-keys`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setFunctionalKeys(Array.isArray(data) ? data : data?.data || []);
      } catch (err) {
        setFunctionalKeys([]);
      }
    };
    fetchFunctionalKeys();
  }, [baseUrl]);

  // Rate box focused: F1–F5 fill the admin percentage for that key. F10 opens monthly ₹.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isRateOfInterestFocused) return;
      if (!event.key || !event.key.startsWith('F')) return;
      const key = event.key.toUpperCase();
      if (key === 'F10') {
        event.preventDefault();
        setShowRateOfInterestPopup(true);
        return;
      }
      if (!/^F[1-5]$/.test(key)) return;
      event.preventDefault();
      const matchedKey = functionalKeys.find((fk) => String(fk.keyName || '').toUpperCase() === key);
      const assigned = matchedKey != null ? Number(matchedKey.percentage) : NaN;
      if (!matchedKey || !Number.isFinite(assigned)) return;
      setFunctionalKey(matchedKey._id);
      setPledgeData((prev) => ({
        ...prev,
        loanDetails: {
          ...prev.loanDetails,
          rateOfInterest: assigned
        }
      }));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [functionalKeys, isRateOfInterestFocused]);

  // Fetch articles on component mount
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}/articles`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await response.json();
        setArticles(data);
        data.forEach((article, index) => {
        });
      } catch (error) {
      }
    };

    fetchArticles();
  }, []);

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${baseUrl}/ledgers?type=Customer`);
        const data = await response.json();
        const fetchedCustomers = Array.isArray(data.data) ? data.data : data.data.customers;
        setCustomers(fetchedCustomers);
      } catch (error) {
      }
    };
  
    fetchCustomers();
  }, []);

  // Fetch clients based on search term
  useEffect(() => {
    const fetchClients = async () => {
      if (searchTerm) {
        try {
          const response = await fetch(`${baseUrl}/ledgers?type=Customer&search=${searchTerm}`);
          const data = await response.json();
          setClients(data.data);
        } catch (error) {
        }
      } else {
        setClients([]);
      }
    };

    fetchClients();
  }, [searchTerm]);

  useEffect(() => {
    if (!selectedClient) return;
    setSearchTerm(formatCustomerName(selectedClient));
  }, [selectedClient]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onPointerDown = (event) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [dropdownOpen]);

  // Fetch client pledges when a client is selected
  useEffect(() => {
    const fetchClientPledges = async () => {
      if (selectedClient) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${baseUrl}/pledges/client/${selectedClient.code}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          const data = await response.json();
          
          if (response.ok) {
            // Filter to only show CREATED/ACTIVE pledges (not RELEASED)
            const createdPledges = (data.pledges || []).filter(pledge => 
              pledge.status === 'CREATED' || pledge.status === 'ACTIVE' || 
              (!pledge.status && !pledge.loanDetails?.released)
            );
            setClientPledges(createdPledges);
          } else {
            setClientPledges([]);
          }
        } catch (error) {
          setClientPledges([]);
        }
      } else {
        setClientPledges([]);
      }
    };

    fetchClientPledges();
  }, [selectedClient]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setPledgeData(prev => ({
      ...prev,
      customerCode: client.code
    }));
  };

  const handleArticleSelect = (index, e) => {
    const selectedArticleId = e.target.value; 
    const selectedArticle = articles.find(article => article._id === selectedArticleId); 


    const newArticles = [...pledgeData.articles];
    newArticles[index].articleId = selectedArticle ? selectedArticle._id : ''; 
    newArticles[index].description = selectedArticle ? selectedArticle.description : '';
    newArticles[index].valueTotal =
      selectedArticle && selectedArticle.valueTotal !== '' && selectedArticle.valueTotal != null
        ? formatInrAmount(selectedArticle.valueTotal)
        : '';

    const articleIds = newArticles.filter((a) => a.articleId).map((a) => a.articleId);
    const hasMultiple = new Set(articleIds).size > 1;
    setHasMultipleArticles(hasMultiple);

    setPledgeData({
      ...pledgeData,
      articles: newArticles,
      loanDetails: {
        ...pledgeData.loanDetails,
        rateOfInterest: !hasMultiple && selectedArticle
          ? (isBlackLogin()
              ? (blackFormRate(null, selectedArticle) || selectedArticle.percentage)
              : selectedArticle.percentage)
          : pledgeData.loanDetails.rateOfInterest
      }
    });
  };

  const handleArticleChange = (index, e) => {
    const { name, value } = e.target;
    const newArticles = [...pledgeData.articles];

    newArticles[index][name] = value;

    setPledgeData({ ...pledgeData, articles: newArticles });
  };

  const handleLoanDetailChange = (e) => {
    const { name, value, checked } = e.target;
    
    // Update the loan details state
    setPledgeData(prev => ({
      ...prev,
      loanDetails: {
        ...prev.loanDetails,
        [name]: name === 'paidExcess' ? checked : value
      }
    }));
  };

  const handlePrincipalAmountInputChange = (e) => {
    const v = sanitizeAmountInput(e.target.value);
    setPledgeData((prev) => ({
      ...prev,
      loanDetails: { ...prev.loanDetails, principalAmount: v }
    }));
  };

  const handlePrincipalAmountFocus = () => {
    setPledgeData((prev) => {
      const raw = parseLocaleAmount(prev.loanDetails.principalAmount);
      return {
        ...prev,
        loanDetails: {
          ...prev.loanDetails,
          principalAmount: Number.isNaN(raw) ? '' : String(raw)
        }
      };
    });
  };

  const handlePrincipalAmountBlur = () => {
    setPledgeData((prev) => {
      const raw = parseLocaleAmount(prev.loanDetails.principalAmount);
      const next = Number.isNaN(raw) ? '' : formatInrAmount(raw);
      return {
        ...prev,
        loanDetails: { ...prev.loanDetails, principalAmount: next }
      };
    });
  };

  const handleDurationChange = (e) => {
    const value = e.target.value;
    setPledgeData(prev => ({
      ...prev,
      loanDetails: {
        ...prev.loanDetails,
        duration: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // During post-submit flow:
    // - TICKET/CONFIRM: block submit
    // - EDIT: treat submit as "Save"
    if (flowStep === 'EDIT') {
      await handleSaveDraft();
      return;
    }
    if (flowStep !== 'FORM') return;
    // Don't submit if clicking on view photo button
    if (e.target.closest('.view-client-photo-btn')) {
      return;
    }
    
    // Validate required fields
    if (!pledgeData.customerCode) {
        setError('Customer code is required.');
        return;
    }
    const articleInvalid = pledgeData.articles.some((article) => {
      const vt = parseLocaleAmount(article.valueTotal);
      const gw = parseFloat(String(article.grossWeight ?? '').replace(/,/g, ''));
      return (
        !article.articleId ||
        !article.description?.trim() ||
        article.valueTotal === '' ||
        Number.isNaN(vt) ||
        vt <= 0 ||
        article.grossWeight === '' ||
        Number.isNaN(gw) ||
        gw <= 0
      );
    });
    if (pledgeData.articles.length === 0 || articleInvalid) {
        setError('All article fields must be filled including weights.');
        return;
    }
    const principalNum = parseLocaleAmount(pledgeData.loanDetails.principalAmount);
    if (
      pledgeData.loanDetails.principalAmount === '' ||
      Number.isNaN(principalNum) ||
      principalNum <= 0 ||
      !pledgeData.loanDetails.duration
    ) {
        setError('Principal amount and duration are required.');
        return;
    }
    if (!hasMultipleArticles && !pledgeData.loanDetails.rateOfInterest) {
        setError('Rate of interest is required (select an article).');
        return;
    }
    if (hasMultipleArticles) {
      const monthly = parseFloat(pledgeData.loanDetails.monthlyInterest) || parseFloat(monthlyInterestAmount);
      if (!monthly || monthly <= 0) {
        setError('For multiple articles (e.g. Gold + Silver), press F10 on Rate and enter monthly interest ₹.');
        return;
      }
    }

    if (!isEditMode && !skipAccountingOnCreate) {
      const dLines = buildDisbursementPaymentModesPayload();
      const sum = dLines.reduce((s, row) => s + (Number(row.amount) || 0), 0);
      for (const row of dLines) {
        if (
          (row.type === 'bank_transfer' || row.type === 'upi') &&
          !(row.bankLedgerId && String(row.bankLedgerId).trim())
        ) {
          setError('Select a bank account for each Bank transfer and UPI line (Ledger Master → General, group 801–900).');
          return;
        }
      }
      if (dLines.length > 0 && Math.abs(sum - principalNum) > 0.02) {
        setError(
          `Disbursement lines must total principal ₹${principalNum.toLocaleString('en-IN', { minimumFractionDigits: 2 })} (now ₹${sum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}).`
        );
        return;
      }
    }

    const payload = {
      customerCode: pledgeData.customerCode,
      date: pledgeDate,
      articles: pledgeData.articles.map((a) => ({
        ...a,
        valueTotal: parseLocaleAmount(a.valueTotal) || 0,
        grossWeight:
          a.grossWeight === '' || a.grossWeight == null
            ? null
            : parseFloat(String(a.grossWeight).replace(/,/g, '')),
        netWeight:
          a.netWeight === '' || a.netWeight == null
            ? null
            : parseFloat(String(a.netWeight).replace(/,/g, '')),
        purity: a.purity,
      })),
      loanDetails: {
          principalAmount: principalNum,
          duration: pledgeData.loanDetails.duration,
          rateOfInterest: isBlackLogin() && isEditMode
            ? (legalRateRef.current === '' ? undefined : Number(legalRateRef.current))
            : (Number(pledgeData.loanDetails.rateOfInterest) || undefined),
          paidExcess: pledgeData.loanDetails.paidExcess
      },
      fingerprint: pledgeData.fingerprint || null,
      disbursementPaymentModes: buildDisbursementPaymentModesPayload(),
      monthlyInterestAmount:
        parseFloat(pledgeData.loanDetails.monthlyInterest) || parseFloat(monthlyInterestAmount) || 0,
      functionalKey: functionalKey || null,
    };

    // Accounting posting control:
    // - no-accounting page: skip ledger entries on initial pledge POST
    // - normal page: when editing existing pledges, post accounting only if requested
    if (!isEditMode && skipAccountingOnCreate) payload.skipAccountingEntries = true;
    if (secondaryOnlyMode) payload.skipAccountingEntries = true;
    if (isEditMode && postAccountingOnEdit) payload.postAccountingEntries = true;

    const hasPhotos = !!(pledgeData.clientPhoto instanceof File && pledgeData.clientPhoto) || !!(pledgeData.articlePhoto instanceof File && pledgeData.articlePhoto);
    const useFormData = hasPhotos;


    try {
      const token = localStorage.getItem('token');
      const createUrl =
        createEndpoint ||
        (secondaryOnlyMode ? `${baseUrl}/pledges/secondary-only` : `${baseUrl}/pledges/pledges`);
      const url = isEditMode && editPledgeId
        ? `${baseUrl}/pledges/${editPledgeId}`
        : createUrl;
      const method = isEditMode && editPledgeId ? 'PUT' : 'POST';

      let body;
      const headers = { 'Authorization': `Bearer ${token}` };
      if (useFormData) {
        const fd = new FormData();
        fd.append('customerCode', payload.customerCode);
        fd.append('date', payload.date);
        fd.append('articles', JSON.stringify(payload.articles));
        fd.append('loanDetails', JSON.stringify(payload.loanDetails));
        fd.append('monthlyInterestAmount', String(payload.monthlyInterestAmount || 0));
        if (payload.functionalKey) fd.append('functionalKey', String(payload.functionalKey));
        fd.append('fingerprint', payload.fingerprint ? JSON.stringify(payload.fingerprint) : '');
        if (payload.skipAccountingEntries) fd.append('skipAccountingEntries', 'true');
        if (payload.postAccountingEntries) fd.append('postAccountingEntries', 'true');
        fd.append('disbursementPaymentModes', JSON.stringify(payload.disbursementPaymentModes || []));
        if (pledgeData.clientPhoto instanceof File) fd.append('clientPhoto', pledgeData.clientPhoto);
        if (pledgeData.articlePhoto instanceof File) fd.append('articlePhoto', pledgeData.articlePhoto);
        body = fd;
        // Do not set Content-Type; browser sets multipart/form-data with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(payload);
      }
      
      const response = await fetch(url, {
        method,
        headers,
        body
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Error creating pledge');
      }

      if (isEditMode) {
        setMessage(`Pledge updated successfully!`);
        setError('');
      } else if (secondaryOnlyMode) {
        const tNo =
          data.ticketNo ||
          data.illegalTransaction?.loanDetails?.ticketNo ||
          data.pledge?.loanDetails?.ticketNo ||
          '';
        setMessage(`Secondary pledge created. Ticket No: ${tNo}`);
        setError('');
        setFlowStep('FORM');
        setDraftPledgeId(null);
        setTicketNoInput('');
      } else {
        // Move focus to Ticket No field and enter the keyboard-driven flow
        setDraftPledgeId(data.pledge?._id || null);
        setTicketNoInput(data.pledge?.loanDetails?.ticketNo || '');
        setFlowStep('TICKET');
        setError('');
        setMessage(`Pledge created. Ticket No: ${data.pledge.loanDetails.ticketNo}. Change ticket no if needed and press Enter.`);
        setTimeout(() => ticketNoInputRef.current?.focus(), 50);
        return; // IMPORTANT: do not clear form; user will confirm via Enter/Generate flow
      }
      // Keep existing behavior for edit mode only (no create-flow).
      if (isEditMode) {
        setFingerprintData(null);
        setIsFingerprintScannerOpen(false);
      }
    } catch (error) {
      setError(error.message || 'Error creating pledge');
    }
  };

  const handleShowModal = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPhoto('');
  };

  const handleAddArticle = () => {
    const newArticles = [...pledgeData.articles, {
      articleId: '',
      description: '',
      valueTotal: '',
      grossWeight: '',
      netWeight: '',
      purity: ''
    }];
    setPledgeData({ ...pledgeData, articles: newArticles });

    // Recheck if we have multiple articles
    const hasMultiple = checkDifferentArticleIds();
    setHasMultipleArticles(hasMultiple);
  };

  const handleRemoveArticle = (index) => {
    const newArticles = [...pledgeData.articles];
    newArticles.splice(index, 1);
    setPledgeData({ ...pledgeData, articles: newArticles });

    // Recheck if we still have multiple articles
    const hasMultiple = checkDifferentArticleIds();
    setHasMultipleArticles(hasMultiple);
  };

  const captureFingerprint = () => {
  };

  const checkDifferentArticleIds = () => {
    const articleIds = pledgeData.articles
      .filter(article => article.articleId)
      .map(article => article.articleId);
    return new Set(articleIds).size > 1;
  };

  const checkSameArticleId = () => {
    const articleIds = pledgeData.articles
      .filter(article => article.articleId)
      .map(article => article.articleId);
    return articleIds.length > 0 && new Set(articleIds).size === 1;
  };

  const handleViewPledgeDetails = (pledge) => {
    setSelectedPledge(pledge);
    setShowPledgeDetails(true);
  };

  const handleClosePledgeDetails = () => {
    setSelectedPledge(null);
    setShowPledgeDetails(false);
  };

  const handleTicketClick = (ticketNo) => {
    // Find the pledge with the matching ticket number
    const pledge = clientPledges.find(p => p.loanDetails.ticketNo === ticketNo);
    if (pledge) {
      setTicketDetails(pledge);
      setShowTicketPopup(true);
    }
  };

  const handleCloseTicketPopup = () => {
    setShowTicketPopup(false);
    setTicketDetails(null);
  };

  const handleRateOfInterestSubmit = (e) => {
    if (e) e.preventDefault();
    const amount = parseFloat(monthlyInterestAmount);
    if (!isNaN(amount) && amount > 0) {
      const newArticles = pledgeData.articles.map((article) => ({
        ...article,
        monthlyInterest: amount
      }));
      setPledgeData((prev) => ({
        ...prev,
        articles: newArticles,
        loanDetails: {
          ...prev.loanDetails,
          monthlyInterest: amount
        }
      }));
      setShowRateOfInterestPopup(false);
    }
  };

  const handleCustomerChange = (e) => {
    const code = e.target.value;
    const selectedCustomer =
      customers.find((c) => String(c.code) === String(code)) ||
      (clients || []).find((c) => String(c.code) === String(code));
    if (selectedCustomer) {
      setPledgeData(prev => ({
        ...prev,
        customerCode: selectedCustomer.code
      }));
      setSelectedClient(selectedCustomer);
      setDropdownOpen(false);
    }
  };

  const focusPrincipalAmount = () => {
    scrollToAndFocus(principalAmountRef.current);
  };

  const focusDuration = () => {
    scrollToAndFocus(durationRef.current);
  };

  const focusRateOfInterest = () => {
    scrollToAndFocus(rateOfInterestRef.current);
  };

  const scrollToAndFocus = (el) => {
    const doScroll = () => {
      if (!el) return;
      try {
        el.focus?.();
      } catch {}

      const container = formScrollRef.current;
      if (container) {
        const elRect = el.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();
        const delta = elRect.top - cRect.top;
        const target = Math.max(0, container.scrollTop + delta - 80);
        container.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    setTimeout(doScroll, 50);
    setTimeout(doScroll, 200);
    setTimeout(doScroll, 400);
  };

  const setArticleRef = (index, key) => (el) => {
    if (!articleFieldRefs.current[index]) articleFieldRefs.current[index] = {};
    articleFieldRefs.current[index][key] = el;
  };

  const focusArticleField = (index, key) => {
    const el = articleFieldRefs.current?.[index]?.[key];
    scrollToAndFocus(el);
  };

  const selectCustomerByCode = (code) => {
    handleCustomerChange({ target: { value: code } });
    // Prefer full search-result / list row so photos are present
    const fromSearch = (clients || []).find((c) => String(c.code) === String(code));
    const fromAll = (customers || []).find((c) => String(c.code) === String(code));
    const picked = fromSearch || fromAll;
    if (picked) setSelectedClient(picked);
    // After selecting customer, move to Article ID
    focusArticleField(0, 'articleId');
  };

  const handleScanFingerprintForCustomer = async () => {
    setScanningForCustomer(true);
    setError('');
    setMessage('');
    try {
      const data = await fingerprintService.searchPledgesByScan();
      const matches = data?.matches || [];
      if (matches.length === 0) {
        setError('No matching customer found. Customer must have an active pledge with fingerprint registered.');
        return;
      }
      const pledge = matches[0];
      const code = pledge.customer?.code;
      if (!code) {
        setError('Matched pledge has no customer code.');
        return;
      }
      const clientResponse = await fetch(`${baseUrl}/ledgers?type=Customer&code=${code}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const clientData = await clientResponse.json();
      const ledger = Array.isArray(clientData.data) && clientData.data.length > 0 ? clientData.data[0] : null;
      if (ledger) {
        setSelectedClient(ledger);
        setPledgeData(prev => ({ ...prev, customerCode: code }));
        setMessage('Customer selected by fingerprint. Enter article details.');
        setTimeout(() => focusArticleField(0, 'articleId'), 150);
      } else {
        setSelectedClient(pledge.customer ? { ...pledge.customer, code: pledge.customer.code } : null);
        setPledgeData(prev => ({ ...prev, customerCode: code }));
        setMessage('Customer selected by fingerprint. Enter article details.');
        setTimeout(() => focusArticleField(0, 'articleId'), 150);
      }
    } catch (err) {
      setError(err.message || 'Fingerprint scan failed. Ensure scanner is connected and try again.');
    } finally {
      setScanningForCustomer(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setDropdownOpen(true);
    if (selectedClient) {
      const label = formatCustomerName(selectedClient);
      if (value.trim() !== label) {
        setSelectedClient(null);
        setPledgeData((prev) => ({ ...prev, customerCode: '' }));
      }
    }
  };

  const filteredCustomers = (() => {
    const q = String(searchTerm || '').toLowerCase().trim();
    const source = Array.isArray(clients) && clients.length > 0 ? clients : customers;
    if (!q) return Array.isArray(source) ? source.slice(0, 80) : [];
    return (source || []).filter((customer) => {
      const name = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase();
      const mobile = String(customer.mobileNo || '');
      const code = String(customer.code || '').toLowerCase();
      return name.includes(q) || mobile.includes(q) || code.includes(q);
    });
  })();

  const handleInputChange = (event, field) => {
    const value = event.target.value.toUpperCase();
    setPledgeData(prevData => ({
      ...prevData,
      [field]: value
    }));
  };

  const handleArticleInputChange = (event, index, field) => {
    const raw = event.target.value;
    let value;
    if (field === 'description') {
      value = raw.toUpperCase();
    } else if (field === 'purity') {
      value = raw;
    } else if (field === 'grossWeight' || field === 'netWeight') {
      value = sanitizeDecimalInput(raw);
    } else if (field === 'valueTotal') {
      value = sanitizeAmountInput(raw);
    } else {
      value = raw.toUpperCase();
    }
    setPledgeData((prevData) => ({
      ...prevData,
      articles: prevData.articles.map((article, i) =>
        i === index ? { ...article, [field]: value } : article
      )
    }));
  };

  const handleArticleWeightBlur = (index, field) => {
    if (field !== 'grossWeight' && field !== 'netWeight') return;
    setPledgeData((prev) => ({
      ...prev,
      articles: prev.articles.map((article, i) => {
        if (i !== index) return article;
        const formatted = formatWeightThreeDecimals(article[field]);
        return { ...article, [field]: formatted };
      })
    }));
  };

  const handleArticleValueFocus = (index) => {
    setPledgeData((prev) => ({
      ...prev,
      articles: prev.articles.map((article, i) => {
        if (i !== index) return article;
        const raw = parseLocaleAmount(article.valueTotal);
        return {
          ...article,
          valueTotal: Number.isNaN(raw) ? '' : String(raw)
        };
      })
    }));
  };

  const handleArticleValueBlur = (index) => {
    setPledgeData((prev) => ({
      ...prev,
      articles: prev.articles.map((article, i) => {
        if (i !== index) return article;
        const raw = parseLocaleAmount(article.valueTotal);
        const next = Number.isNaN(raw) ? '' : formatInrAmount(raw);
        return { ...article, valueTotal: next };
      })
    }));
  };

  const handleLoanDetailsChange = (event) => {
    const { name, value, checked } = event.target;
    
    // Update the loan details state
    setPledgeData(prev => ({
      ...prev,
      loanDetails: {
        ...prev.loanDetails,
        [name]: name === 'paidExcess' ? checked : value
      }
    }));
  };

  const handleAddNewCustomer = () => {
    setDropdownOpen(false);
    setShowAddCustomerModal(true);
  };

  // Force focus on input fields when nested modals open
  useEffect(() => {
    if (showAddCustomerModal) {
      const timer = setTimeout(() => {
        // Aggressive focus management for nested Material-UI modals
        const setupInputFocus = () => {
          // Target all possible input types in Material-UI Dialogs
          const selectors = [
            '.MuiDialog-root input[type="text"]',
            '.MuiDialog-root input[type="email"]',
            '.MuiDialog-root textarea',
            '.MuiDialog-root .MuiInputBase-input',
            '.MuiDialog-root .MuiTextField-root input',
            '.MuiDialog-root .MuiFormControl-root input',
            '.MuiDialog-root input',
            '.MuiDialog-root textarea'
          ];
          
          selectors.forEach(selector => {
            const inputs = document.querySelectorAll(selector);
            inputs.forEach(input => {
              // Remove existing listeners to prevent duplicates
              input.removeEventListener('focus', input.select);
              input.removeEventListener('click', input.focus);
              input.removeEventListener('mousedown', input.focus);
              input.removeEventListener('pointerdown', input.focus);
              
              // Set styles for better interaction
              input.style.pointerEvents = 'auto';
              input.style.zIndex = '9999';
              input.style.cursor = 'text';
              input.style.userSelect = 'text';
              input.style.position = 'relative';
              input.style.backgroundColor = 'white';
              input.style.color = 'black';
              
              // Add comprehensive event listeners
              input.addEventListener('focus', () => {
                input.select();
              });
              input.addEventListener('click', () => {
                input.focus();
              });
              input.addEventListener('mousedown', () => {
                input.focus();
              });
              input.addEventListener('pointerdown', () => {
                input.focus();
              });
              input.addEventListener('touchstart', () => {
                input.focus();
              });
            });
          });
        };
        
        // Initial setup
        setupInputFocus();
        
        // Setup again after multiple delays to catch any late-rendered inputs
        setTimeout(setupInputFocus, 100);
        setTimeout(setupInputFocus, 300);
        setTimeout(setupInputFocus, 500);
        
        // Force focus on the first available input
        const firstInput = document.querySelector('.MuiDialog-root input[autoFocus], .MuiDialog-root .MuiTextField-root input, .MuiDialog-root input[type="text"], .MuiDialog-root input');
        if (firstInput) {
          setTimeout(() => {
            firstInput.focus();
            firstInput.select();
          }, 200);
        }
        
        // Global click handler for nested modal inputs
        const handleGlobalClick = (e) => {
          if (e.target.closest('.MuiDialog-root')) {
            const input = e.target.closest('input') || e.target;
            if (input && (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA')) {
              setTimeout(() => {
                input.focus();
                input.select();
              }, 10);
            }
          }
        };
        
        // Global mousedown handler for better responsiveness
        const handleGlobalMouseDown = (e) => {
          if (e.target.closest('.MuiDialog-root')) {
            const input = e.target.closest('input') || e.target;
            if (input && (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA')) {
              setTimeout(() => {
                input.focus();
              }, 5);
            }
          }
        };
        
        // Global pointerdown handler for modern browsers
        const handleGlobalPointerDown = (e) => {
          if (e.target.closest('.MuiDialog-root')) {
            const input = e.target.closest('input') || e.target;
            if (input && (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA')) {
              setTimeout(() => {
                input.focus();
              }, 5);
            }
          }
        };
        
        document.addEventListener('click', handleGlobalClick);
        document.addEventListener('mousedown', handleGlobalMouseDown);
        document.addEventListener('pointerdown', handleGlobalPointerDown);
        
        // Store cleanup so effect cleanup can run it when modal closes
        addCustomerModalCleanupRef.current = () => {
          document.removeEventListener('click', handleGlobalClick);
          document.removeEventListener('mousedown', handleGlobalMouseDown);
          document.removeEventListener('pointerdown', handleGlobalPointerDown);
          addCustomerModalCleanupRef.current = null;
        };
      }, 200);
      return () => {
        clearTimeout(timer);
        if (addCustomerModalCleanupRef.current) {
          addCustomerModalCleanupRef.current();
        }
      };
    }
  }, [showAddCustomerModal]);

  // When the Add Customer modal is fully closed, bump instance counter so LedgerMaster remounts next open
  useEffect(() => {
    if (!showAddCustomerModal) {
      setAddCustomerModalInstance(prev => prev + 1);
    }
  }, [showAddCustomerModal]);

  // Handle focus management for nested modals
  useEffect(() => {
    if (showAddCustomerModal) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Add CSS to fix Material-UI components in nested modals
      const style = document.createElement('style');
      style.id = 'nested-modal-fix';
      style.textContent = `
        .modal-backdrop {
          z-index: 1040 !important;
        }
        .modal {
          z-index: 1050 !important;
        }
        .MuiDialog-root {
          z-index: 9999 !important;
          position: fixed !important;
        }
        .MuiBackdrop-root {
          z-index: 9998 !important;
        }
        .MuiPopover-root {
          z-index: 99999 !important;
        }
        .MuiPaper-root {
          z-index: 99999 !important;
        }
        .MuiMenu-root {
          z-index: 99999 !important;
        }
        .MuiSelect-select {
          pointer-events: auto !important;
        }
        .MuiFormControl-root,
        .MuiTextField-root,
        .MuiInputBase-root,
        .MuiInputBase-input {
          pointer-events: auto !important;
        }
        .pledge-add-customer-content .MuiInputLabel-root,
        #ledger-master-container .MuiInputLabel-root {
          z-index: 2 !important;
          color: #334155 !important;
          background-color: #f8fafc;
          padding-left: 4px;
          padding-right: 4px;
        }
        .MuiInputBase-input:focus {
          z-index: 1 !important;
          pointer-events: auto !important;
          cursor: text !important;
          user-select: text !important;
        }
        .MuiTextField-root input {
          pointer-events: auto !important;
          cursor: text !important;
          user-select: text !important;
        }
        .MuiDialog-root input {
          pointer-events: auto !important;
          cursor: text !important;
          user-select: text !important;
        }
        .MuiDialog-root textarea {
          pointer-events: auto !important;
          cursor: text !important;
          user-select: text !important;
        }
        #ledger-master-container .MuiDialog-root {
          position: fixed !important;
          z-index: 9999 !important;
        }
        #ledger-master-container .MuiBackdrop-root {
          z-index: 9998 !important;
        }
        .MuiDialog-root .MuiInputBase-root {
          pointer-events: auto !important;
        }
        .MuiDialog-root .MuiTextField-root {
          pointer-events: auto !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
      
      // Remove the CSS fix
      const style = document.getElementById('nested-modal-fix');
      if (style) {
        style.remove();
      }
    }

    return () => {
      // Cleanup: restore body scroll
      document.body.style.overflow = 'unset';
      
      // Remove the CSS fix
      const style = document.getElementById('nested-modal-fix');
      if (style) {
        style.remove();
      }
    };
  }, [showAddCustomerModal]);

  const handleDateChange = (e) => {
    setPledgeDate(e.target.value);
  };

  const handleCustomerCreated = (customer) => {
    if (!customer) return;
    const c = customer.data || customer; // support wrapped { data: ledger } response
    setShowAddCustomerModal(false);
    setCustomers(prev => [...prev, c]);
    setSelectedClient(c);
    setPledgeData(prevData => ({
      ...prevData,
      customerCode: c.code
    }));
    setSearchTerm(`${c.firstName || ''} ${c.lastName || ''}`.trim());
    setMessage('New customer added. Enter article details.');
    setError('');
  };

  const pageTitle = isEditMode
    ? 'Edit Pledge'
    : secondaryOnlyMode
      ? 'Create Secondary Pledge (Black Book)'
      : skipAccountingOnCreate
        ? 'Create Pledge (No Accounting)'
        : 'Create Pledge';
  const pageSubtitle = isEditMode ? 'Pledge · Edit' : 'Pledge · Create';
  const selectedClientPhotoUrl = getClientPhotoUrl(selectedClient);

  return (
    <div className="create-pledge-container">
      <GirviPageShell
        title={pageTitle}
        subtitle={pageSubtitle}
        chipLabel="Pledge"
        maxWidth="xl"
        compact
        actions={
          <div className="d-flex align-items-end gap-2 flex-wrap">
            {secondaryOnlyMode && !isEditMode ? (
              <Badge bg="dark" style={{ fontWeight: 700, alignSelf: 'center' }}>Black book</Badge>
            ) : skipAccountingOnCreate && !isEditMode ? (
              <Badge bg="warning" text="dark" style={{ fontWeight: 700, alignSelf: 'center' }}>No accounting</Badge>
            ) : null}
            <Form.Group controlId="pledgeDate" className="mb-0">
              <Form.Label className="mb-1" style={{ color: 'rgba(245,230,200,0.75)', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Date
              </Form.Label>
              <Form.Control
                type="date"
                value={pledgeDate}
                onChange={handleDateChange}
                style={{ width: 148, borderRadius: 10, border: '1px solid rgba(240,217,160,0.35)', background: 'rgba(255,255,255,0.96)', height: 32, fontSize: 13 }}
              />
            </Form.Group>
          </div>
        }
      >
        <div
          ref={formScrollRef}
          style={{
            maxHeight: 'calc(100vh - 168px)',
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          <form
            onSubmit={handleSubmit}
            className="create-pledge-form"
            onKeyDown={(e) => {
              // Ticket step: Enter validates ticket and opens confirm popup
              if (flowStep === 'TICKET' && e.key === 'Enter' && e.target === ticketNoInputRef.current) {
                e.preventDefault();
                handleTicketEnter();
                return;
              }
              // Edit step: Enter opens confirm popup
              if (flowStep === 'EDIT' && e.key === 'Enter' && !showConfirmPopup) {
                e.preventDefault();
                handleOpenConfirmPopup();
              }
            }}
            onClick={(e) => {
              // Prevent form submission if clicking on the eye icon
              if (e.target.closest('.view-client-photo-btn')) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
                {/* Ticket No + Customer — compact search with auto photo */}
                <div className="mb-3">
                  <Row className="g-3 align-items-stretch">
                    <Col xs={12} md={3} lg={2}>
                      <Form.Group controlId="ticketNo">
                        <Form.Label style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>Ticket No</Form.Label>
                        <Form.Control
                          ref={ticketNoInputRef}
                          type="text"
                          value={ticketNoInput}
                          onChange={(e) => setTicketNoInput(e.target.value)}
                          placeholder={draftPledgeId ? 'Enter / edit ticket no' : 'Submit pledge to get ticket no'}
                          style={{ borderRadius: 10 }}
                        />
                        {draftPledgeId && (flowStep === 'TICKET' || flowStep === 'EDIT') ? (
                          <Form.Text className="text-muted">
                            {flowStep === 'TICKET'
                              ? 'Press Enter to confirm ticket no (warns if already taken).'
                              : 'Press Enter to preview pledge (Generate/Edit).'}
                          </Form.Text>
                        ) : null}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={9} lg={10}>
                      <Form.Group controlId="customerCode">
                        <Form.Label style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>Customer</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="flex-grow-1 position-relative" ref={customerSearchRef}>
                            <Form.Control
                              type="text"
                              placeholder="Search by name, mobile, code..."
                              value={searchTerm}
                              onChange={handleSearch}
                              disabled={flowStep === 'TICKET'}
                              autoComplete="off"
                              style={{ borderRadius: 10 }}
                              onFocus={() => {
                                if (flowStep !== 'TICKET') setDropdownOpen(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (filteredCustomers.length > 0) {
                                    selectCustomerByCode(filteredCustomers[0].code);
                                    setDropdownOpen(false);
                                  }
                                } else if (e.key === 'Escape') {
                                  setDropdownOpen(false);
                                }
                              }}
                            />
                          {dropdownOpen && flowStep !== 'TICKET' && (
                          <div
                            className="w-100 bg-white"
                            style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40, minWidth: '100%', maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 16px 40px rgba(15,23,42,0.14)' }}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {filteredCustomers.length === 0 ? (
                              <div className="px-3 py-2 text-muted">No customers found</div>
                            ) : (
                              filteredCustomers.map(customer => {
                                const thumbUrl = getClientPhotoUrl(customer);
                                return (
                                <button
                                  type="button"
                                  key={customer.code}
                                  onClick={() => {
                                    selectCustomerByCode(customer.code);
                                    setDropdownOpen(false);
                                  }}
                                  className="cp-customer-result"
                                  style={{ minWidth: '100%' }}
                                >
                                  <div className="d-flex gap-2 align-items-start w-100">
                                    <div
                                      style={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 10,
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                        background: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        display: 'grid',
                                        placeItems: 'center',
                                      }}
                                    >
                                      {thumbUrl ? (
                                        <img
                                          src={thumbUrl}
                                          alt=""
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>N/A</span>
                                      )}
                                    </div>
                                    <div className="w-100" style={{ minWidth: 0 }}>
                                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-1">
                                      <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
                                        <span className="badge bg-secondary">{customer.code}</span>
                                        <strong className="text-wrap">{formatCustomerName(customer)}</strong>
                                      </div>
                                      <div className="d-flex gap-1">
                                        {customer.photos?.aadharCard && (
                                          <Button 
                                            variant="link" 
                                            size="sm"
                                            className="p-1"
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (e.nativeEvent) {
                                                e.nativeEvent.stopImmediatePropagation();
                                              }
                                              handleShowModal(customer.photos.aadharCard);
                                            }}
                                          >
                                            <FaEye title="View Aadhar" />
                                          </Button>
                                        )}
                                        {customer.photos?.panCard && (
                                          <Button 
                                            variant="link" 
                                            size="sm"
                                            className="p-1"
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (e.nativeEvent) {
                                                e.nativeEvent.stopImmediatePropagation();
                                              }
                                              handleShowModal(customer.photos.panCard);
                                            }}
                                          >
                                            <FaEye title="View Pan" />
                                          </Button>
                                        )}
                                        {customer.photos?.clientPhoto && (
                                          <Button 
                                            variant="link" 
                                            size="sm"
                                            className="p-1"
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (e.nativeEvent) {
                                                e.nativeEvent.stopImmediatePropagation();
                                              }
                                              handleShowModal(customer.photos.clientPhoto);
                                            }}
                                          >
                                            <FaEye title="View Client Photo" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="text-muted small">
                                      <div className="d-flex flex-wrap gap-3 align-items-center">
                                        <span><strong>Mobile:</strong> {customer.mobileNo}</span>
                                        <span><strong>Caste:</strong> {customer.caste || 'N/A'}</span>
                                        <span><strong>Village:</strong> {customer.village || 'N/A'}</span>
                                      </div>
                                    </div>
                                    </div>
                                  </div>
                                </button>
                              );})
                            )}
                          </div>
                          )}
                          </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          type="button"
                          onClick={handleAddNewCustomer}
                          disabled={flowStep === 'TICKET'}
                          style={{ borderRadius: 10, whiteSpace: 'nowrap', height: 38 }}
                        >
                          Add New
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={handleScanFingerprintForCustomer}
                          disabled={scanningForCustomer}
                          title="Scan fingerprint to select customer"
                          className="d-flex align-items-center gap-1"
                          style={{ minWidth: '42px', height: '38px', borderRadius: 10 }}
                        >
                          {scanningForCustomer ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaFingerprint />
                          )}
                          <span className="d-none d-md-inline">Scan</span>
                        </Button>
                        {selectedClientPhotoUrl ? (
                          <button
                            type="button"
                            className="view-client-photo-btn border-0 p-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleShowModal(getClientStoredPhoto(selectedClient));
                            }}
                            title="Click to zoom photo"
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 10,
                              overflow: 'hidden',
                              flexShrink: 0,
                              cursor: 'zoom-in',
                              border: '1px solid rgba(201,162,77,0.55)',
                              boxShadow: '0 2px 8px rgba(15,23,42,0.12)',
                              background: '#0b1220',
                            }}
                          >
                            <img
                              src={selectedClientPhotoUrl}
                              alt={`${selectedClient?.firstName || ''} ${selectedClient?.lastName || ''}`.trim() || 'Client'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              onError={(e) => {
                                e.currentTarget.style.opacity = '0.35';
                              }}
                            />
                          </button>
                        ) : null}
                      </div>
                    </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Lock pledge editing until ticket is confirmed */}
                <fieldset disabled={flowStep === 'TICKET'}>
                  <div className="form-section">
                  {selectedClient && (
                    <Row className="mb-3">
                      <Col>
                        <div className="cp-client-card">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                            <h5 style={{ margin: 0, fontSize: 15, fontWeight: 780, color: '#0f172a' }}>Client details</h5>
                            <div className="d-flex align-items-center gap-2">
                              {selectedClientPhotoUrl ? (
                                <button
                                  type="button"
                                  className="view-client-photo-btn border-0 p-0"
                                  onClick={() => handleShowModal(getClientStoredPhoto(selectedClient))}
                                  title="Click to zoom photo"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    cursor: 'zoom-in',
                                    border: '1px solid #cbd5e1',
                                    background: '#fff',
                                  }}
                                >
                                  <img
                                    src={selectedClientPhotoUrl}
                                    alt="Client"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                </button>
                              ) : null}
                              <Badge bg="dark" style={{ fontWeight: 700 }}>{selectedClient.code}</Badge>
                            </div>
                          </div>
                          <div className="d-flex flex-wrap gap-3" style={{ fontSize: 13, color: '#334155' }}>
                            <span><strong>Name:</strong> {selectedClient.firstName} {selectedClient.lastName}</span>
                            <span><strong>Mobile:</strong> {selectedClient.mobileNo || '—'}</span>
                            <span><strong>Caste:</strong> {selectedClient.caste || '—'}</span>
                            <span><strong>Occupation:</strong> {selectedClient.occupation || '—'}</span>
                            <span><strong>Village:</strong> {selectedClient.village || '—'}</span>
                            <span><strong>At Post:</strong> {selectedClient.atPost || '—'}</span>
                            <span><strong>Taluka:</strong> {selectedClient.taluka || '—'}</span>
                            <span><strong>District:</strong> {selectedClient.district || '—'}</span>
                          </div>
                          <div className="mt-2" style={{ fontSize: 13, color: '#475569', wordBreak: 'break-word' }}>
                            <strong>Address:</strong> {selectedClient.address || '—'}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                 
                  {/* Pledge Articles Section */}
                  <div className="mb-4">
                    <h5 className="mb-3" style={{ fontSize: 15, fontWeight: 780, color: '#0f172a' }}>Pledge Articles</h5>
                    <Row>
                      <Col>
                      <div className="table-responsive">
                        <Table bordered hover size="sm" className="align-middle" style={{ borderRadius: 12, overflow: 'hidden' }}>
                          <thead style={{ background: '#f8fafc' }}>
                            <tr>
                              <th style={{ minWidth: 88 }}>Article</th>
                              <th style={{ minWidth: 140 }}>Description</th>
                              <th style={{ minWidth: 88, width: 96 }}>Gross (g)</th>
                              <th style={{ minWidth: 88, width: 96 }}>Net (g)</th>
                              <th style={{ minWidth: 64, width: 72 }}>Purity</th>
                              <th style={{ minWidth: 88, width: 100 }}>Value</th>
                              <th style={{ width: 72, padding: '4px' }} />
                            </tr>
                          </thead>
                          <tbody>
                            {pledgeData.articles.map((article, index) => (
                              <tr key={index}>
                                <td>
                                  <Form.Group controlId={`articleId-${index}`}> 
                                    <Form.Control
                                      as="select"
                                      name="articleId"
                                      value={article.articleId}
                                      onChange={(e) => handleArticleSelect(index, e)}
                                      ref={setArticleRef(index, 'articleId')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          focusArticleField(index, 'description');
                                        }
                                      }}
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '2px solid #495057',
                                        borderRadius: '4px',
                                        fontWeight: '500'
                                      }}
                                    >
                                      <option value="">Select Article</option>
                                      {articles.map((article) => (
                                        <option key={article._id} value={article._id}>{article.title}</option>
                                      ))}
                                    </Form.Control>
                                  </Form.Group>
                                </td>
                                <td style={{ minWidth: 220, width: '42%' }}>
                                  <Form.Group controlId={`description-${index}`}>
                                    <Form.Control
                                      type="text"
                                      name="description"
                                      value={article.description}
                                      onChange={(e) => handleArticleInputChange(e, index, 'description')}
                                      ref={setArticleRef(index, 'description')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          focusArticleField(index, 'grossWeight');
                                        }
                                      }}
                                      required
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '2px solid #495057',
                                        borderRadius: '4px',
                                        fontWeight: '500',
                                        minWidth: 0
                                      }}
                                    />
                                  </Form.Group>
                                </td>
                                <td>
                                  <Form.Group controlId={`grossWeight-${index}`}> 
                                    <Form.Control
                                      type="text"
                                      inputMode="decimal"
                                      name="grossWeight"
                                      value={article.grossWeight}
                                      onChange={(e) => handleArticleInputChange(e, index, 'grossWeight')}
                                      onBlur={() => handleArticleWeightBlur(index, 'grossWeight')}
                                      ref={setArticleRef(index, 'grossWeight')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleArticleWeightBlur(index, 'grossWeight');
                                          focusArticleField(index, 'netWeight');
                                        }
                                      }}
                                      required
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '2px solid #495057',
                                        borderRadius: '4px',
                                        fontWeight: '500'
                                      }}
                                    />
                                  </Form.Group>
                                </td>
                                <td>
                                  <Form.Group controlId={`netWeight-${index}`}> 
                                    <Form.Control
                                      type="text"
                                      inputMode="decimal"
                                      name="netWeight"
                                      value={article.netWeight}
                                      onChange={(e) => handleArticleInputChange(e, index, 'netWeight')}
                                      onBlur={() => handleArticleWeightBlur(index, 'netWeight')}
                                      ref={setArticleRef(index, 'netWeight')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleArticleWeightBlur(index, 'netWeight');
                                          focusArticleField(index, 'purity');
                                        }
                                      }}
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '2px solid #495057',
                                        borderRadius: '4px',
                                        fontWeight: '500'
                                      }}
                                    />
                                  </Form.Group>
                                </td>
                                <td style={{ width: 92, maxWidth: 108, paddingLeft: 4, paddingRight: 4 }}>
                                  <Form.Group controlId={`purity-${index}`}> 
                                    <Form.Control
                                      type="text"
                                      name="purity"
                                      value={article.purity || ''}
                                      onChange={(e) => handleArticleInputChange(e, index, 'purity')}
                                      ref={setArticleRef(index, 'purity')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          focusArticleField(index, 'valueTotal');
                                        }
                                      }}
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '2px solid #495057',
                                        borderRadius: '4px',
                                        fontWeight: '500',
                                        maxWidth: '100%',
                                        paddingLeft: 6,
                                        paddingRight: 6
                                      }}
                                    />
                                  </Form.Group>
                                </td>
                                <td>
                                  <Form.Group controlId={`valueTotal-${index}`}>
                                    <Form.Control
                                      type="text"
                                      inputMode="decimal"
                                      name="valueTotal"
                                      value={article.valueTotal}
                                      onChange={(e) => handleArticleInputChange(e, index, 'valueTotal')}
                                      onFocus={() => handleArticleValueFocus(index)}
                                      onBlur={() => handleArticleValueBlur(index)}
                                      ref={setArticleRef(index, 'valueTotal')}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleArticleValueBlur(index);
                                          focusPrincipalAmount();
                                        }
                                      }}
                                      required
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '2px solid #495057',
                                        borderRadius: '4px',
                                        fontWeight: '500'
                                      }}
                                    />
                                  </Form.Group>
                                </td>
                                <td className="text-center p-1">
                                  <div className="d-flex align-items-center justify-content-center gap-1">
                                    {index === pledgeData.articles.length - 1 && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      className="cp-row-ico"
                                      onClick={handleAddArticle}
                                      title="Add article"
                                      type="button"
                                    >
                                      <FaPlus />
                                    </Button>
                                    )}
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="cp-row-ico"
                                      onClick={() => handleRemoveArticle(index)}
                                      title="Remove article"
                                      type="button"
                                      disabled={pledgeData.articles.length <= 1}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      </Col>
                    </Row>
                    </div>
                  {/* Loan Details Section */}
                  <div style={{ 
                    backgroundColor: '#ffffff', 
                    padding: '20px', 
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    marginBottom: '24px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}>
                    <h5 style={{ 
                      marginBottom: '12px', 
                      color: '#212529', 
                      fontWeight: '600',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #dee2e6'
                    }}>
                      Loan Details
                    </h5>
                    <Row className="g-2 align-items-end">
                      <Col xs={12} md={4}>
                        <Form.Group controlId="principalAmount">
                          <Form.Label>Principal Amount</Form.Label>
                          <Form.Control
                            ref={principalAmountRef}
                            type="text"
                            inputMode="decimal"
                            name="principalAmount"
                            value={pledgeData.loanDetails.principalAmount}
                            onChange={handlePrincipalAmountInputChange}
                            onFocus={handlePrincipalAmountFocus}
                            onBlur={handlePrincipalAmountBlur}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handlePrincipalAmountBlur();
                                if (flowStep === 'FORM') {
                                  focusDuration();
                                }
                              }
                            }}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group controlId="duration">
                          <Form.Label>Duration (months)</Form.Label>
                          <Form.Control
                            ref={durationRef}
                            type="number"
                            name="duration"
                            value={pledgeData.loanDetails.duration}
                            onChange={handleLoanDetailsChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (flowStep === 'FORM') {
                                  focusRateOfInterest();
                                }
                              }
                            }}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group controlId="articleRateOfInterest">
                          <Form.Label>
                            Article Rate of Interest (%)
                            {functionalKey
                              ? ` · F-key: ${
                                  functionalKeys.find((fk) => fk._id === functionalKey)?.keyName || 'set'
                                }`
                              : ' · F1–F5 illegal · F10 monthly'}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            name="rateOfInterest"
                            value={pledgeData.loanDetails.rateOfInterest}
                            onChange={handleLoanDetailsChange}
                            ref={rateOfInterestRef}
                            onFocus={() => setIsRateOfInterestFocused(true)}
                            onBlur={() => setIsRateOfInterestFocused(false)}
                            readOnly={hasMultipleArticles}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (flowStep === 'FORM') {
                                  submitPledgeBtnRef.current?.click();
                                }
                              }
                            }}
                            required={!hasMultipleArticles}
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px solid #495057',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }}
                          />
                          {hasMultipleArticles && (
                            <Form.Text muted>
                              Multi-article: press F10 and enter total monthly ₹
                              {pledgeData.loanDetails.monthlyInterest
                                ? ` (₹${pledgeData.loanDetails.monthlyInterest})`
                                : ''}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  <div className="mt-3">
                    
                    
                    <Table striped bordered hover size="sm" className="mb-2">
                      <thead>
                        <tr>
                          <th>Payment type</th>
                          <th>Bank account</th>
                          <th>Amount</th>
                          <th style={{ width: 48 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {disbursementPaymentModes.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <Form.Select
                                value={row.type}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setDisbursementPaymentModes((prev) => {
                                    const next = [...prev];
                                    const patch = { type: v };
                                    if (v === 'cash') {
                                      patch.bankLedgerId = '';
                                      patch.bankLedgerCode = '';
                                      patch.bankLedgerName = '';
                                    }
                                    next[index] = { ...next[index], ...patch };
                                    return next;
                                  });
                                }}
                              >
                                <option value="cash">Cash</option>
                                <option value="bank_transfer">Bank transfer</option>
                                <option value="upi">UPI</option>
                              </Form.Select>
                            </td>
                            <td>
                              {row.type === 'cash' ? (
                                <span className="text-muted small">—</span>
                              ) : (
                                <Form.Select
                                  value={row.bankLedgerId || ''}
                                  onChange={(e) => {
                                    const id = e.target.value;
                                    const b = bankAccountsList.find((x) => String(x._id) === id);
                                    setDisbursementPaymentModes((prev) => {
                                      const next = [...prev];
                                      next[index] = {
                                        ...next[index],
                                        bankLedgerId: id,
                                        bankLedgerCode: b?.code || '',
                                        bankLedgerName: b
                                          ? `${b.firstName || ''} ${b.lastName || ''}`.trim()
                                          : ''
                                      };
                                      return next;
                                    });
                                  }}
                                >
                                  <option value="">Select bank account</option>
                                  {bankAccountsList.map((b) => (
                                    <option key={b._id} value={b._id}>
                                      {b.code} — {`${b.firstName || ''} ${b.lastName || ''}`.trim()}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                inputMode="decimal"
                                placeholder="Amount"
                                value={row.amount}
                                onChange={(e) => handleDisbursementRowChange(index, 'amount', e.target.value)}
                              />
                            </td>
                            <td className="text-center p-1">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="py-0 px-1"
                                disabled={disbursementPaymentModes.length <= 1}
                                onClick={() => handleRemoveDisbursementRow(index)}
                                title="Remove row"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Button variant="outline-primary" size="sm" onClick={handleAddDisbursementRow}>
                      + Add payment line
                    </Button>
                  </div>
                  </div>
                  
                  {/* Photos & Fingerprint Section */}
                  <div className="mb-4">
                    <h5 className="mb-3" style={{ fontSize: 15, fontWeight: 780, color: '#0f172a' }}>Photos & Fingerprint</h5>
                    <Row className="cp-media-grid g-2">
                      <Col>
                        <div className="photo-section mb-3">
                          <h6 className="mb-2">Client Photo</h6>
                        <div className="photo-options d-flex gap-3 mb-3">
                          <Button variant="outline-primary" size="sm" onClick={() => handleCameraOpen('client')} className="d-flex align-items-center">
                            <i className="fas fa-camera me-2"></i>
                            <span>Take Photo</span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'clientPhoto')}
                            className="d-none"
                            id="clientPhotoInput"
                          />
                          <Button variant="outline-secondary" size="sm" onClick={() => document.getElementById('clientPhotoInput').click()} className="d-flex align-items-center">
                            <i className="fas fa-upload me-2"></i>
                            <span>Choose File</span>
                          </Button>
                        </div>
                        {pledgeData.clientPhoto && (
                          <div className="photo-preview-container mt-3">
                            <Card className="border-0 shadow-sm">
                              <Card.Img variant="top" src={URL.createObjectURL(pledgeData.clientPhoto)} className="photo-preview" style={{ 
                                width: '100%',
                                maxHeight: '160px',
                                objectFit: 'contain',
                                background: '#0b1220'
                              }} />
                              <Card.Body className="d-flex align-items-center justify-content-between p-3">
                                <Card.Title className="mb-0">Client Photo</Card.Title>
                                <Button variant="danger" size="sm" onClick={() => setPledgeData(prev => ({ ...prev, clientPhoto: null }))} className="d-flex align-items-center">
                                  <i className="fas fa-trash me-1"></i>
                                  Remove
                                </Button>
                              </Card.Body>
                            </Card>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col>
                      <div className="photo-section mb-3">
                        <h6 className="mb-2">Article Photo</h6>
                        <div className="photo-options d-flex gap-3 mb-3">
                          <Button variant="outline-primary" size="sm" onClick={() => handleCameraOpen('article')} className="d-flex align-items-center">
                            <i className="fas fa-camera me-2"></i>
                            <span>Take Photo</span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'articlePhoto')}
                            className="d-none"
                            id="articlePhotoInput"
                          />
                          <Button variant="outline-secondary" size="sm" onClick={() => document.getElementById('articlePhotoInput').click()} className="d-flex align-items-center">
                            <i className="fas fa-upload me-2"></i>
                            <span>Choose File</span>
                          </Button>
                        </div>
                        {pledgeData.articlePhoto && (
                          <div className="photo-preview-container mt-3">
                            <Card className="border-0 shadow-sm">
                              <Card.Img variant="top" src={URL.createObjectURL(pledgeData.articlePhoto)} className="photo-preview" style={{ 
                                width: '100%',
                                maxHeight: '160px',
                                objectFit: 'contain',
                                background: '#0b1220'
                              }} />
                              <Card.Body className="d-flex align-items-center justify-content-between p-3">
                                <Card.Title className="mb-0">Article Photo</Card.Title>
                                <Button variant="danger" size="sm" onClick={() => setPledgeData(prev => ({ ...prev, articlePhoto: null }))} className="d-flex align-items-center">
                                  <i className="fas fa-trash me-1"></i>
                                  Remove
                                </Button>
                              </Card.Body>
                            </Card>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col>
                      <div className="fingerprint-section mb-3">
                        <h6 className="mb-2">Customer Fingerprint</h6>
                        <div className="d-flex flex-column align-items-start gap-3">
                          <div className="d-flex gap-2 align-items-center" style={{ flexWrap: 'nowrap' }}>
                            <Button 
                              variant={fingerprintData ? 'outline-primary' : 'primary'}
                              size="sm"
                              onClick={() => setIsFingerprintScannerOpen(true)}
                              className="d-flex align-items-center"
                              style={{ whiteSpace: 'nowrap', minWidth: '150px' }}
                            >
                              <FaFingerprint className="me-2" />
                              {fingerprintData ? 'Rescan Fingerprint' : 'Scan Fingerprint'}
                            </Button>
                            {selectedClient && (
                              <Button 
                                variant="info"
                                onClick={() => {
                                  setShowPledgesModal(true);
                                }}
                                className="d-flex align-items-center"
                                style={{ whiteSpace: 'nowrap', minWidth: '150px' }}
                              >
                                <FaList className="me-2" />
                                View Pledges {clientPledges.length > 0 && `(${clientPledges.length})`}
                              </Button>
                            )}
                          </div>
                          
                          {fingerprintData && (
                            <div className="w-100">
                              <div 
                                className="border rounded p-2 d-inline-block cursor-pointer"
                                onClick={() => {
                                  setSelectedPhoto(fingerprintData.image);
                                  setShowPhotoPreview(true);
                                }}
                              >
                                <img 
                                  src={fingerprintData.image} 
                                  alt="Captured Fingerprint" 
                                  style={{ 
                                    maxWidth: '200px',
                                    maxHeight: '100px',
                                    display: 'block'
                                  }} 
                                />
                              </div>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="text-danger mt-2 p-0 d-block"
                                onClick={() => {
                                  setFingerprintData(null);
                                  setPledgeData(prev => ({
                                    ...prev,
                                    fingerprint: null
                                  }));
                                }}
                              >
                                <FaTimes className="me-1" /> Remove
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  </div>
                  
                  {/* Submit Button Section */}
                  <div className="cp-submit-bar">
                    <Row>
                      <Col>
                        <Button 
                          ref={submitPledgeBtnRef}
                          type="submit" 
                          variant="primary" 
                          className="w-100"
                          disabled={flowStep === 'TICKET' || flowStep === 'CONFIRM'}
                        >
                          {flowStep === 'FORM'
                            ? 'Submit Pledge'
                            : flowStep === 'EDIT'
                              ? 'Save Pledge'
                              : 'Submit Locked (confirm ticket/preview)'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {message && <Alert variant="success">{message}</Alert>}
                  </div>
                </fieldset>
              </form>
        </div>
      </GirviPageShell>

      {/* Footer removed - pledges now shown in modal */}

      {/* Message Display */}
      <div className="message-container">
        {error && <div className="error-message">{error}</div>}
        {message && (
          <div className="success-message">
            <div className="success-message-content" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {message}
            </div>
          </div>
        )}
      </div>

      {/* Ticket Taken Warning Popup */}
      <Modal
        show={showTicketTakenPopup}
        onHide={() => {
          setShowTicketTakenPopup(false);
          setTimeout(() => ticketNoInputRef.current?.focus(), 50);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Ticket No Already Taken</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This Ticket No is already used. Please change the Ticket No and press Enter again.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setShowTicketTakenPopup(false);
              setTimeout(() => ticketNoInputRef.current?.focus(), 50);
            }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm / Generate Popup */}
      <Modal
        show={showConfirmPopup}
        onHide={closeConfirmPopup}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Pledge - Ticket No: {confirmPreview?.ticketNo || ticketNoInput}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2 text-muted">
            Press <strong>Enter</strong> to Generate or press <strong>N</strong> to Edit.
          </div>

          {confirmPreview && (
            <div>
              <div className="d-flex justify-content-between align-items-start mb-3 p-3 bg-light rounded">
                <div>
                  <div><strong>Date:</strong> {formatPledgeDateDDMMYYYY(confirmPreview.date)}</div>
                  <div>
                    <strong>Customer:</strong> {confirmPreview.customer?.firstName || ''} {confirmPreview.customer?.lastName || ''}{' '}
                    {confirmPreview.customer?.code ? `(${confirmPreview.customer.code})` : ''}
                  </div>
                  {confirmPreview.loanDetails?.monthlyInterest != null &&
                    confirmPreview.loanDetails.monthlyInterest !== '' && (
                    <div><strong>Monthly Interest:</strong> ₹{confirmPreview.loanDetails.monthlyInterest}</div>
                  )}
                </div>
                <div className="text-end">
                  <div><strong>Total Value:</strong> ₹{formatInrAmount(confirmPreview.totals?.totalValue || 0)}</div>
                  {confirmPreview.articleTypeTotals && confirmPreview.articleTypeTotals.length >= 2 ? (
                    confirmPreview.articleTypeTotals.map((typeTotal, idx) => (
                      <div key={idx}>
                        <div><strong>{typeTotal.title} Gross Weight:</strong> {formatWeightThreeDecimals(typeTotal.totalGrossWeight)} g</div>
                      </div>
                    ))
                  ) : (
                    <>
                      <div><strong>Total Gross:</strong> {formatWeightThreeDecimals(confirmPreview.totals?.totalGrossWeight)} g</div>
                      <div><strong>Total Net:</strong> {formatWeightThreeDecimals(confirmPreview.totals?.totalNetWeight)} g</div>
                    </>
                  )}
                </div>
              </div>

              {(confirmPhotoUrls.client || confirmPhotoUrls.article) && (
                <>
                  <h6 className="mt-3">Photos</h6>
                  <Row className="mb-3 g-3">
                    {confirmPhotoUrls.client && (
                      <Col xs={6} md={4}>
                        <Card className="h-100 border shadow-sm">
                          <Card.Img
                            variant="top"
                            src={confirmPhotoUrls.client}
                            alt="Client"
                            style={{ height: 160, objectFit: 'cover' }}
                          />
                          <Card.Body className="py-2 text-center">
                            <small className="text-muted">Client Photo</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {confirmPhotoUrls.article && (
                      <Col xs={6} md={4}>
                        <Card className="h-100 border shadow-sm">
                          <Card.Img
                            variant="top"
                            src={confirmPhotoUrls.article}
                            alt="Article"
                            style={{ height: 160, objectFit: 'cover' }}
                          />
                          <Card.Body className="py-2 text-center">
                            <small className="text-muted">Article Photo</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              <h6>Articles</h6>
              <Table striped bordered hover size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th style={{ width: '34%' }}>Description</th>
                    <th style={{ width: '18%' }}>Value</th>
                    <th style={{ width: '24%' }}>Gross</th>
                    <th style={{ width: '24%' }}>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {(confirmPreview.articles || []).map((a, idx) => (
                    <tr key={idx}>
                      <td style={{ wordBreak: 'break-word' }}>{a.description}</td>
                      <td>₹{formatInrAmount(parseLocaleAmount(a.valueTotal) || 0)}</td>
                      <td>{formatWeightThreeDecimals(a.grossWeight)} g</td>
                      <td>
                        {formatWeightThreeDecimals(
                          a.netWeight === '' || a.netWeight == null ? 0 : a.netWeight
                        )}{' '}
                        g
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6 className="mt-3">Loan Details</h6>
              <div className="d-flex flex-wrap gap-4 p-2 bg-light rounded">
                <div><strong>Principal:</strong> ₹{formatInrAmount(parseLocaleAmount(confirmPreview.loanDetails?.principalAmount) || 0)}</div>
                <div><strong>Duration:</strong> {confirmPreview.loanDetails?.duration || ''} months</div>
                <div><strong>Rate:</strong> {confirmPreview.loanDetails?.rateOfInterest || '—'}%</div>
                <div><strong>Paid Excess:</strong> {confirmPreview.loanDetails?.paidExcess ? 'Yes' : 'No'}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeConfirmPopup}>
            Edit (N)
          </Button>
          <Button variant="primary" onClick={handleGenerateFromPopup}>
            Generate (Enter)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Ticket Popup */}
      <Modal 
        show={showTicketPopup} 
        onHide={handleCloseTicketPopup}
        size="lg"
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Pledge Details - Ticket No: {ticketDetails?.loanDetails?.ticketNo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ticketDetails && (
            <div className="pledge-details-container">
              <div className="pledge-header mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <p className="mb-1">Date: {formatPledgeDateFromApi(ticketDetails.date)}</p>
                    <p className="mb-1">Customer: {selectedClient?.firstName} {selectedClient?.lastName}</p>
                  </div>
                  <div className="text-end">
                    <p className="mb-1">Total Value: ₹{formatInrAmount(parseFloat(ticketDetails.totalValue) || 0)}</p>
                    <p className="mb-1">Rate of Interest: {rateLabel(ticketDetails, isBlackLogin())}</p>
                  </div>
                </div>
              </div>

              <div className="articles-section">
                <h5 className="mb-3">Pledged Articles</h5>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Value</th>
                      <th>Gross Weight</th>
                      <th>Net Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketDetails.articles.map((article, index) => (
                      <tr key={index}>
                        <td>{article.description}</td>
                        <td>₹{formatInrAmount(parseFloat(article.valueTotal) || 0)}</td>
                        <td>{formatWeightThreeDecimals(article.grossWeight)} g</td>
                        <td>{formatWeightThreeDecimals(article.netWeight == null || article.netWeight === '' ? 0 : article.netWeight)} g</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="loan-details-section mt-4">
                <h5 className="mb-3">Loan Details</h5>
                <div className="loan-details-grid">
                  <div className="loan-detail-item">
                    <label>Principal Amount:</label>
                    <span>₹{formatInrAmount(parseFloat(ticketDetails.loanDetails.principalAmount) || 0)}</span>
                  </div>
                  <div className="loan-detail-item">
                    <label>Duration:</label>
                    <span>{ticketDetails.loanDetails.duration} months</span>
                  </div>
                  <div className="loan-detail-item">
                    <label>Rate of Interest:</label>
                    <span>{rateLabel(ticketDetails, isBlackLogin())}</span>
                  </div>
                  <div className="loan-detail-item">
                    <label>Paid Excess:</label>
                    <span>{ticketDetails.loanDetails.paidExcess ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {ticketDetails.fingerprint && (
                <div className="fingerprint-section mt-4">
                  <h5 className="mb-3">Customer Fingerprint</h5>
                  <div className="fingerprint-container">
                    {/* Add fingerprint image here if available */}
                    <p>Fingerprint data is stored securely</p>
                  </div>
                </div>
              )}

              {(ticketDetails.clientPhoto || ticketDetails.articlePhoto) && (
                <div className="photos-section mt-4">
                  <h5 className="mb-3">Photos</h5>
                  <Row>
                    {ticketDetails.clientPhoto && (
                      <Col md={6} className="mb-3">
                        <Card>
                          <Card.Img
                            variant="top"
                            src={resolvePledgePhotoUrl(ticketDetails.clientPhoto)}
                            alt="Client Photo"
                            className="photo-card"
                          />
                          <Card.Body>
                            <Card.Title>Client Photo</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {ticketDetails.articlePhoto && (
                      <Col md={6} className="mb-3">
                        <Card>
                          <Card.Img
                            variant="top"
                            src={resolvePledgePhotoUrl(ticketDetails.articlePhoto)}
                            alt="Article Photo"
                            className="photo-card"
                          />
                          <Card.Body>
                            <Card.Title>Article Photo</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* View Pledges Modal */}
      <Modal 
        show={showPledgesModal} 
        onHide={() => setShowPledgesModal(false)}
        size="lg"
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Client Pledges
            {selectedClient && (
              <> - {selectedClient.firstName || ''} {selectedClient.lastName || ''} {selectedClient.code ? `(${selectedClient.code})` : ''}</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {clientPledges.length === 0 ? (
            <div className="text-center py-4">
              <p>No active pledges found for this client.</p>
              <p className="text-muted small">Only CREATED/ACTIVE pledges are shown (RELEASED pledges are excluded).</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover size="sm" style={{ minWidth: '600px' }}>
                <thead>
                  <tr>
                    <th>Ticket No</th>
                    <th>Date</th>
                    <th>Total Value</th>
                    <th>Gross Weight</th>
                    <th>Net Weight</th>
                    <th>Rate of Interest</th>
                    <th>Status</th>
                  </tr>
                </thead>
              <tbody>
                {clientPledges.map((pledge) => {
                  const totalValue = parseFloat(pledge.totalValue) || 0;
                  const totalGrossWeight = parseFloat(pledge.totalGrossWeight) || 0;
                  const totalNetWeight = parseFloat(pledge.totalNetWeight) || 0;
                  return (
                    <tr key={pledge._id}>
                      <td 
                        onClick={() => {
                          handleTicketClick(pledge.loanDetails.ticketNo);
                          setShowPledgesModal(false);
                        }} 
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                      >
                        {pledge.loanDetails?.ticketNo || 'N/A'}
                      </td>
                      <td>{formatPledgeDateFromApi(pledge.date)}</td>
                      <td>₹{formatInrAmount(parseFloat(totalValue) || 0)}</td>
                      <td>{formatWeightThreeDecimals(totalGrossWeight)} g</td>
                      <td>{formatWeightThreeDecimals(totalNetWeight)} g</td>
                      <td>{rateLabel(pledge, isBlackLogin())}</td>
                      <td>
                        <Badge bg={pledge.status === 'CREATED' || pledge.status === 'ACTIVE' ? 'success' : 'secondary'}>
                          {pledge.status || 'ACTIVE'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPledgesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Camera Modal */}
      <Modal show={isCameraOpen} onHide={handleCameraClose} size="md" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Capture {photoType === 'client' ? 'Client' : 'Article'} Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column align-items-center">
            {!showPhotoPreview ? (
              <>
                <div className="camera-preview-container" style={{ 
                  width: '100%', 
                  maxWidth: '400px',
                  position: 'relative',
                  aspectRatio: '4/3'
                }}>
                  <video ref={videoRef} autoPlay playsInline className="camera-preview" style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }} />
                  <canvas ref={canvasRef} className="camera-canvas d-none" />
                </div>
                <div className="camera-controls w-100 mt-3">
                  <div className="d-flex justify-content-center gap-2">
                    <Button variant="success" onClick={handleTakePhoto} className="me-2">
                      <i className="fas fa-camera me-1"></i>
                      <span>Take Photo</span>
                    </Button>
                    <Button variant="secondary" onClick={handleCameraClose}>
                      <i className="fas fa-times me-1"></i>Cancel
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="photo-preview-container" style={{
                width: '100%',
                maxWidth: '400px',
                height: '300px',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <img 
                  src={capturedPhoto?.url} 
                  alt="Preview" 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}
            {showPhotoPreview && (
              <div className="d-flex justify-content-center gap-2 mt-3">
                <Button variant="success" onClick={handleConfirmPhoto} className="me-2">
                  <i className="fas fa-check me-1"></i>Confirm Photo
                </Button>
                <Button variant="warning" onClick={handleRetakePhoto} className="me-2">
                  <i className="fas fa-redo me-1"></i>Retake Photo
                </Button>
                <Button variant="secondary" onClick={handleCameraClose}>
                  <i className="fas fa-times me-1"></i>Cancel
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Pledge Details Popup */}
      <Modal 
        show={showPledgeDetails} 
        onHide={handleClosePledgeDetails}
        size="lg"
        scrollable={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Pledge Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPledge && (
            <div className="pledge-details-container">
              <div className="pledge-header mb-4">
                <h4 className="text-center">Pledge Ticket No: {selectedPledge.loanDetails.ticketNo}</h4>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <p className="mb-1">Date: {formatPledgeDateFromApi(selectedPledge.date)}</p>
                    <p className="mb-1">Customer: {selectedClient.firstName} {selectedClient.lastName}</p>
                  </div>
                  <div className="text-end">
                    <p className="mb-1">Total Value: ₹{formatInrAmount(parseFloat(selectedPledge.totalValue) || 0)}</p>
                    <p className="mb-1">Rate of Interest: {rateLabel(selectedPledge, isBlackLogin())}</p>
                  </div>
                </div>
              </div>

              <div className="articles-section">
                <h5 className="mb-3">Pledged Articles</h5>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Value</th>
                      <th>Gross Weight</th>
                      <th>Net Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPledge.articles.map((article, index) => (
                      <tr key={index}>
                        <td>{article.description}</td>
                        <td>₹{formatInrAmount(parseFloat(article.valueTotal) || 0)}</td>
                        <td>{formatWeightThreeDecimals(article.grossWeight)} g</td>
                        <td>{formatWeightThreeDecimals(article.netWeight == null || article.netWeight === '' ? 0 : article.netWeight)} g</td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    {(() => {
                      const typeTotals = computeArticleTypeWeightTotals(selectedPledge.articles, articles);
                      if (typeTotals.length >= 2) {
                        return typeTotals.map((t, idx) => (
                          <tr key={idx}>
                            <td colSpan={2} />
                            <td colSpan={2}>
                              <strong>{t.title} Gross Weight:</strong>{' '}
                              {formatWeightThreeDecimals(t.totalGrossWeight)} g
                            </td>
                          </tr>
                        ));
                      }
                      return (
                        <tr>
                          <td colSpan={2} />
                          <td>Total Gross Weight: {formatWeightThreeDecimals(selectedPledge.totalGrossWeight)} g</td>
                          <td>Total Net Weight: {formatWeightThreeDecimals(selectedPledge.totalNetWeight)} g</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </Table>
              </div>

              <div className="loan-details-section mt-4">
                <h5 className="mb-3">Loan Details</h5>
                <div className="loan-details-grid">
                  <div className="loan-detail-item">
                    <label>Principal Amount:</label>
                    <span>₹{formatInrAmount(parseFloat(selectedPledge.loanDetails.principalAmount) || 0)}</span>
                  </div>
                  <div className="loan-detail-item">
                    <label>Duration:</label>
                    <span>{selectedPledge.loanDetails.duration} months</span>
                  </div>
                  <div className="loan-detail-item">
                    <label>Rate of Interest:</label>
                    <span>{rateLabel(selectedPledge, isBlackLogin())}</span>
                  </div>
                  <div className="loan-detail-item">
                    <label>Paid Excess:</label>
                    <span>{selectedPledge.loanDetails.paidExcess ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {selectedPledge.fingerprint && (
                <div className="fingerprint-section mt-4">
                  <h5 className="mb-3">Customer Fingerprint</h5>
                  <div className="fingerprint-container">
                    {/* Add fingerprint image here if available */}
                    <p>Fingerprint data is stored securely</p>
                  </div>
                </div>
              )}

              {(selectedPledge.clientPhoto || selectedPledge.articlePhoto) && (
                <div className="photos-section mt-4">
                  <h5 className="mb-3">Photos</h5>
                  <Row>
                    {selectedPledge.clientPhoto && (
                      <Col md={6} className="mb-3">
                        <Card>
                          <Card.Img
                            variant="top"
                            src={resolvePledgePhotoUrl(selectedPledge.clientPhoto)}
                            alt="Client Photo"
                            className="photo-card"
                          />
                          <Card.Body>
                            <Card.Title>Client Photo</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {selectedPledge.articlePhoto && (
                      <Col md={6} className="mb-3">
                        <Card>
                          <Card.Img
                            variant="top"
                            src={resolvePledgePhotoUrl(selectedPledge.articlePhoto)}
                            alt="Article Photo"
                            className="photo-card"
                          />
                          <Card.Body>
                            <Card.Title>Article Photo</Card.Title>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Fingerprint Scanner Modal */}
      <Modal 
        show={isFingerprintScannerOpen} 
        onHide={() => setIsFingerprintScannerOpen(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Fingerprint Scanner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center">
            <canvas 
              ref={fingerprintCanvasRef} 
              style={{ 
                border: '1px solid #ddd', 
                width: '100%', 
                maxWidth: '300px',
                height: '150px',
                backgroundColor: '#f8f9fa'
              }} 
            />
          </div>
          <div className="text-center mt-3">
            <Button 
              variant="primary" 
              onClick={handleFingerprintCapture}
              disabled={isCapturingFingerprint}
              className="d-inline-flex align-items-center"
            >
              {isCapturingFingerprint ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Capturing...
                </>
              ) : (
                <>
                  <FaFingerprint className="me-2" />
                  Capture Fingerprint
                </>
              )}
            </Button>
          </div>
          {fingerprintError && (
            <Alert variant="danger" className="mt-3">
              {fingerprintError}
            </Alert>
          )}
        </Modal.Body>
      </Modal>

      {/* Zoom client / document photo */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        contentClassName="border-0 bg-transparent"
        dialogClassName="modal-dialog-centered"
      >
        <div
          style={{
            position: 'relative',
            background: '#0b1220',
            borderRadius: 16,
            border: '1px solid rgba(212,175,106,0.35)',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          }}
        >
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div style={{ color: '#f5e6c8', fontWeight: 750, fontSize: 15 }}>
              {selectedClient
                ? `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim() || 'Client photo'
                : 'Photo'}
            </div>
            <Button
              type="button"
              variant="light"
              size="sm"
              onClick={handleCloseModal}
              aria-label="Close photo"
              title="Close"
              className="d-inline-flex align-items-center justify-content-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 18,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </Button>
          </div>
          <div className="text-center p-3">
            {selectedPhoto ? (
              <Image
                src={resolvePledgePhotoUrl(selectedPhoto) || selectedPhoto}
                fluid
                rounded
                style={{ maxHeight: '72vh', objectFit: 'contain' }}
                alt="Client photo zoom"
              />
            ) : (
              <p className="text-light mb-0">No photo available</p>
            )}
          </div>
        </div>
      </Modal>

      {/* F10 Monthly Interest Setup */}
      <Modal
        show={showRateOfInterestPopup}
        onHide={() => setShowRateOfInterestPopup(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Monthly Interest Setup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <h5>Total monthly interest (₹) for multi-article pledges</h5>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={monthlyInterestAmount}
                onChange={(e) => setMonthlyInterestAmount(e.target.value)}
                style={{ width: '160px' }}
                placeholder="Enter amount"
                autoFocus
              />
              <span style={{ alignSelf: 'center' }}>₹</span>
            </div>
          </div>
          <div className="mt-3 text-end">
            <Button variant="primary" onClick={handleRateOfInterestSubmit}>
              Save
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add New Customer Modal */}
      <Modal 
        show={showAddCustomerModal} 
        onHide={() => setShowAddCustomerModal(false)}
        scrollable
        backdrop={false}
        keyboard={false}
        style={{ zIndex: 2000 }}
        dialogClassName="pledge-add-customer-dialog"
        contentClassName="pledge-add-customer-content"
        enforceFocus={false}
        restoreFocus={false}
      >
        <style>{`
          .modal:has(.pledge-add-customer-dialog) {
            z-index: 2000 !important;
            top: 60px !important;
            height: calc(100vh - 60px) !important;
            overflow: hidden !important;
            display: flex !important;
            align-items: flex-start !important;
            padding: 8px 12px 12px !important;
            box-sizing: border-box;
          }
          .pledge-add-customer-dialog {
            max-width: 920px;
            width: 100%;
            height: 100% !important;
            max-height: 100% !important;
            margin: 0 auto !important;
            min-height: 0 !important;
            display: flex !important;
            align-items: stretch !important;
          }
          .pledge-add-customer-content {
            border: none;
            border-radius: 16px;
            overflow: hidden;
            width: 100%;
            height: 100%;
            max-height: 100%;
            display: flex;
            flex-direction: column;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
          }
          .pledge-add-customer-content .modal-header {
            flex: 0 0 auto;
            display: flex !important;
            background: linear-gradient(180deg, #0b1220, #1a2d4a);
            color: #f5e6c8;
            border: none;
            padding: 10px 16px;
          }
          .pledge-add-customer-content .modal-title {
            font-size: 15px;
            font-weight: 750;
            letter-spacing: 0.02em;
          }
          .pledge-add-customer-content .btn-close {
            filter: invert(1) grayscale(1) brightness(2);
          }
          .pledge-add-customer-content .modal-body {
            flex: 1 1 auto;
            min-height: 0;
            max-height: none;
            padding: 8px 14px 12px;
            background: #f8fafc;
            overflow-y: auto;
          }
          .pledge-add-customer-content legend {
            float: unset;
            width: auto;
            max-width: 100%;
            padding: 0;
            margin: 0;
            font-size: inherit;
            line-height: inherit;
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>Add New Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div id="ledger-master-container">
            <LedgerMaster 
              key={addCustomerModalInstance}
              isModal={true}
              onClose={() => setShowAddCustomerModal(false)}
              onCustomerCreated={handleCustomerCreated}
            />
          </div>
        </Modal.Body>
      </Modal>

     
    </div>
  );
};

export default CreatePledge;